import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Combobox } from '@/components/Combobox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, FileText, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Upload, Download, X, File, Image as ImageIcon, Eye, Copy, Search, Loader2, User, Truck } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import BulkActionsToolbar from '@/components/BulkActionsToolbar';
import CustomCheckbox from '@/components/Checkbox';
import { LabelWithTooltip } from '@/components/Tooltip';
import Wizard from '@/components/Wizard';
import Pagination from '@/components/Pagination';
import { usePermissions } from '@/contexts/PermissionsContext';
import { ProtectedButton, ProtectedSection } from '@/components/rbac';
import LocationAutocomplete from '@/components/LocationAutocomplete';
import RealtimeMapOSM from '@/components/RealtimeMapOSM';

const Operations = () => {
  // Onglet actif (liste ou carte)
  const [activeTab, setActiveTab] = useState('liste');
  const { hasPermission, userRole } = usePermissions();
  
  // Rôles autorisés à voir les tarifs client
  const canViewClientPricing = ['SuperAdmin', 'Admin', 'Manager', 'Commercial', 'Finance'].includes(userRole);
  const [operations, setOperations] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [proprietaires, setProprietaires] = useState([]);
  const [clients, setClients] = useState([]);
  const [typesCamion, setTypesCamion] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [users, setUsers] = useState([]);  // Pour l'assignation d'opérateur
  
  // Track auto-filled fields
  const [autoFilledFields, setAutoFilledFields] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // États pour Mapbox - calcul automatique de distance avec caching
  const [originLocation, setOriginLocation] = useState(null);
  const [destinationLocation, setDestinationLocation] = useState(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [calculatedDistance, setCalculatedDistance] = useState(null);
  
  // États pour les étapes intermédiaires
  const [intermediateSteps, setIntermediateSteps] = useState([]);
  
  // Plus besoin de uniqueClients, on utilise directement la liste clients de la table
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [operationToClose, setOperationToClose] = useState(null);
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [operationToBill, setOperationToBill] = useState(null);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [billingData, setBillingData] = useState({
    tarif_transporteur: '',
    marge_percent: '25',
    tarif_client: ''
  });
  const [closureData, setClosureData] = useState({
    date_depart_reel: '',
    heure_depart_reel: '',
    date_arrivee_reel: '',
    heure_arrivee_reel: '',
    incidents_rencontres: false,
    bon_chargement_file: null,
    bon_dechargement_file: null
  });
  
  const [incidentData, setIncidentData] = useState({
    type_incident: 'Panne',
    gravite: 'Moyenne',
    description: '',
    piece_jointe: null
  });
  
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  
  // Wizard state for closure modal
  const [closureWizardStep, setClosureWizardStep] = useState(0);
  
  // Wizard steps configuration
  const closureWizardSteps = [
    {
      title: "Dates & Heures",
      description: "Dates réelles de chargement et déchargement"
    },
    {
      title: "Documents",
      description: "Bons de chargement et déchargement"
    },
    {
      title: "Incidents",
      description: "Déclaration d'incidents (si nécessaire)"
    },
    {
      title: "Récapitulatif",
      description: "Vérification finale"
    }
  ];
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [documentsStats, setDocumentsStats] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [currentOperationId, setCurrentOperationId] = useState(null);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Filters and sorting state
  const [filters, setFilters] = useState({
    search: '',
    statut: 'all',
    dateFrom: '',
    dateTo: '',
    chauffeur: 'all',
    transporteur: 'all',
    client: 'all',
    immatriculation: '',
    dateChargementFrom: '',
    dateChargementTo: '',
    dateDechargementFrom: '',
    dateDechargementTo: '',
    type_vehicule: 'all',
    type_marchandise: '',
    ville_depart: '',
    ville_destination: '',
    is_multi_destination: 'all'
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'date_operation',
    direction: 'desc'
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Advanced filters toggle
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  const [formData, setFormData] = useState({
    date_operation: new Date().toISOString().split('T')[0],
    operateur: '',
    operateur_assigne: '',  // ID de l'utilisateur assigné
    statut: 'Nouveau',  // Statut par défaut
    chauffeur_id: '',
    proprietaire_id: '',
    client_id: '',  // ID du client
    numero_contact: '',
    type_camion_id: '',
    nb_camions: 1,
    capacite_t: '',
    semi_remorque: false,
    tracteur: '',
    pays: 'Côte d\'Ivoire',
    intra_inter_land: 'Intraland',
    // Nouveaux champs
    ville_depart: '',
    ville_destination: '',
    etapes: [],  // Étapes intermédiaires [{ordre, ville, coords: {lat, lng}}]
    tarif_transport: '',
    tarif_transporteur: '',
    transporteur: '',
    jours_immobilisation: '',
    tarif_immobilisation_journalier: '',
    commentaire_operateur: '',
    // Nouveaux champs logique métier
    type_transporteur: 'chauffeur',  // Par défaut : chauffeur indépendant
    chauffeur_telephone: '',  // Contact réel du chauffeur exécutant
    beneficiaire_paiement_type: 'transporteur',  // Par défaut : le transporteur reçoit
    beneficiaire_paiement_nom: '',
    beneficiaire_paiement_id: '',
    mode_reglement: '',
    date_reglement_prevu: '',
    vehicle_id: '',  // Pour auto-remplissage depuis véhicule
    // Nouveaux champs marchandise et conteneur (Phase 3)
    datetime_chargement: '',  // DEPRECATED - use datetime_chargement_reel
    datetime_dechargement_prevu: '',
    datetime_dechargement_reel: '',
    descriptif_operation: '',
    poids_marchandise: '',
    volume_marchandise: '',
    distance_km: '',
    est_conteneur: false,
    type_conteneur: '',
    type_marchandise: '',
    client_nom: '',
    // Lifecycle management fields (Phase 4)
    datetime_chargement_prevu: '',  // MANDATORY at creation
    datetime_chargement_reel: '',
    bon_chargement_id: '',
    bon_dechargement_id: '',
    jours_immo: '',
    tarif_unitaire_immo: '',
    bon_chargement_file: null,
    bon_dechargement_file: null,
    // Multi-destination field
    is_multi_destination: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Calcul automatique de la distance avec Mapbox (avec caching backend)
  // Prend en compte les étapes intermédiaires si présentes
  useEffect(() => {
    const fetchDistance = async () => {
      if (originLocation && destinationLocation) {
        // Vérifier que toutes les étapes ont des coordonnées avant de calculer
        const etapesSansCoords = intermediateSteps.filter(step => !step.coords || !step.coords.lat || !step.coords.lng);
        
        if (etapesSansCoords.length > 0) {
          console.log(`⏳ Attente des coordonnées pour ${etapesSansCoords.length} étape(s)`);
          // Ne pas calculer si des étapes n'ont pas encore de coordonnées
          return;
        }
        
        setDistanceLoading(true);
        try {
          // Construire la liste des waypoints : départ + étapes + destination
          const waypoints = [
            { lat: originLocation.latitude, lng: originLocation.longitude }
          ];
          
          // Ajouter les étapes intermédiaires (triées par ordre)
          intermediateSteps
            .sort((a, b) => a.ordre - b.ordre)
            .forEach(step => {
              waypoints.push({ lat: step.coords.lat, lng: step.coords.lng });
            });
          
          waypoints.push({ lat: destinationLocation.latitude, lng: destinationLocation.longitude });
          
          console.log(`📍 Calcul distance avec ${waypoints.length} points (${intermediateSteps.length} étapes)`);
          
          // Si il y a des étapes intermédiaires, utiliser l'endpoint multi-step
          const endpoint = waypoints.length > 2 ? '/geo/distance/multi-step' : '/geo/distance';
          const requestData = waypoints.length > 2 
            ? { waypoints, profile: 'driving' }
            : {
                origin: { lat: originLocation.latitude, lng: originLocation.longitude },
                destination: { lat: destinationLocation.latitude, lng: destinationLocation.longitude },
                profile: 'driving'
              };
          
          const response = await api.post(endpoint, requestData);
          
          if (response.data.success) {
            const distanceData = response.data;
            setCalculatedDistance(distanceData);
            setFormData(prev => ({ 
              ...prev, 
              distance_km: distanceData.distance_km.toString()
            }));
            
            // Message adapté selon le nombre d'étapes
            const etapesMsg = waypoints.length > 2 ? ` (${waypoints.length - 2} étape${waypoints.length > 3 ? 's' : ''})` : '';
            if (distanceData.from_cache) {
              toast.success(`Distance: ${distanceData.distance_km} km${etapesMsg} (${distanceData.duration_hours}h) - Cache`);
            } else {
              toast.success(`Distance calculée: ${distanceData.distance_km} km${etapesMsg} (${distanceData.duration_hours}h)`);
            }
          }
        } catch (err) {
          console.error('Erreur calcul distance:', err);
          toast.error('Impossible de calculer la distance automatiquement');
        } finally {
          setDistanceLoading(false);
        }
      }
    };

    fetchDistance();
  }, [originLocation, destinationLocation, intermediateSteps]);

  // Pré-remplir les données de facturation quand une opération est sélectionnée
  useEffect(() => {
    if (operationToBill) {
      const tarifTransp = operationToBill.tarif_transporteur || 0;
      setBillingData({
        tarif_transporteur: tarifTransp.toString(),
        marge_percent: operationToBill.marge_percent?.toString() || '25',
        tarif_client: operationToBill.tarif_client?.toString() || ''
      });
    }
  }, [operationToBill]);
  
  // Calcul automatique de la marge dans le dialogue de facturation
  useEffect(() => {
    if (billingDialogOpen) {
      const tarifTransp = parseFloat(billingData.tarif_transporteur);
      const tarifClient = parseFloat(billingData.tarif_client);
      
      if (tarifTransp && !isNaN(tarifTransp) && tarifTransp > 0 && tarifClient && !isNaN(tarifClient)) {
        // Formule: Marge % = ((Tarif Client - Tarif Transporteur) / Tarif Transporteur) × 100
        const margePercent = ((tarifClient - tarifTransp) / tarifTransp) * 100;
        setBillingData(prev => ({ 
          ...prev, 
          marge_percent: margePercent.toFixed(2)
        }));
      }
    }
  }, [billingData.tarif_transporteur, billingData.tarif_client, billingDialogOpen]);

  // Pré-remplir les données de clôture quand une opération est sélectionnée
  useEffect(() => {
    if (operationToClose) {
      // Extraire date et heure depuis les datetimes existants
      const extractDateTime = (datetime) => {
        if (!datetime) return { date: '', time: '' };
        const dt = datetime.split('T');
        return {
          date: dt[0] || '',
          time: dt[1] ? dt[1].substring(0, 5) : '' // HH:MM
        };
      };

      // Pré-remplir avec les données existantes ou les dates prévues
      const chargementReel = extractDateTime(operationToClose.datetime_chargement_reel || operationToClose.datetime_chargement_prevu);
      const dechargementReel = extractDateTime(operationToClose.datetime_dechargement_reel || operationToClose.datetime_dechargement_prevu);

      setClosureData({
        date_depart_reel: chargementReel.date,
        heure_depart_reel: chargementReel.time,
        date_arrivee_reel: dechargementReel.date,
        heure_arrivee_reel: dechargementReel.time,
        incidents_rencontres: false,
        bon_chargement_file: null,
        bon_dechargement_file: null
      });

      // Réinitialiser l'étape du wizard
      setClosureWizardStep(0);
    }
  }, [operationToClose]);

  const fetchData = async () => {
    try {
      // Ajouter un timestamp pour éviter le cache
      const timestamp = new Date().getTime();
      const [oResponse, cResponse, pResponse, clResponse, tResponse, vResponse] = await Promise.all([
        api.get(`/operations?_t=${timestamp}`),
        api.get('/chauffeurs'),
        api.get('/proprietaires'),
        api.get('/clients'),
        api.get('/types-camion'),
        api.get('/vehicules')
      ]);
      setOperations(oResponse.data);
      console.log(`✅ Operations rechargées: ${oResponse.data.length} opérations`);
      setChauffeurs(cResponse.data);
      setProprietaires(pResponse.data);
      setClients(clResponse.data);
      setTypesCamion(tResponse.data);
      setVehicules(vResponse.data);
      
      // Charger les users si l'utilisateur a la permission (optionnel)
      try {
        const uResponse = await api.get('/users');
        setUsers(uResponse.data);
      } catch (userError) {
        // Si l'utilisateur n'a pas la permission users.read (ex: Operateur), on continue sans
        if (userError.response?.status === 403) {
          console.log('ℹ️ Liste utilisateurs non disponible (permissions insuffisantes)');
          setUsers([]);
        } else {
          throw userError;
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill handlers
  const handleVehiculeChange = (vehiculeId) => {
    if (vehiculeId === 'none') {
      setFormData({ ...formData, vehicule_id: '' });
      return;
    }
    
    const vehicule = (vehicules || []).find(v => v.id === vehiculeId);
    if (!vehicule) return;
    
    const updates = {
      vehicule_id: vehiculeId,
      type_camion_id: vehicule.type_camion_id || formData.type_camion_id,
      capacite_t: vehicule.capacite_tonnes || formData.capacite_t,
      semi_remorque: vehicule.semi_remorque || false,
      tracteur: vehicule.immatriculation || formData.tracteur,
      pays: vehicule.pays || formData.pays,
    };
    
    // Auto-fill chauffeur if vehicle has one
    if (vehicule.chauffeur_id) {
      updates.chauffeur_id = vehicule.chauffeur_id;
      const chauffeur = (chauffeurs || []).find(c => c.id === vehicule.chauffeur_id);
      if (chauffeur) {
        updates.numero_contact = chauffeur.telephone;
      }
    }
    
    // Auto-fill proprietaire if vehicle has one
    if (vehicule.proprietaire_id) {
      updates.proprietaire_id = vehicule.proprietaire_id;
    }
    
    setFormData({ ...formData, ...updates });
    setAutoFilledFields(['type_camion_id', 'capacite_t', 'tracteur', 'pays', 'chauffeur_id', 'proprietaire_id', 'numero_contact']);
    toast.success('✓ Données véhicule chargées');
  };
  
  const handleChauffeurChange = (chauffeurId) => {
    if (chauffeurId === 'none') {
      setFormData({ ...formData, chauffeur_id: '' });
      return;
    }
    
    const chauffeur = (chauffeurs || []).find(c => c.id === chauffeurId);
    if (!chauffeur) {
      setFormData({ ...formData, chauffeur_id: chauffeurId });
      return;
    }
    
    setFormData({
      ...formData,
      chauffeur_id: chauffeurId,
      numero_contact: chauffeur.telephone || formData.numero_contact,
      pays: chauffeur.pays || formData.pays,
    });
    
    setAutoFilledFields(prev => [...new Set([...prev, 'numero_contact', 'pays'])]);
  };
  
  const handleProprietaireChange = (proprietaireId) => {
    // Gérer le cas "Aucun"
    if (proprietaireId === 'none') {
      setFormData({ ...formData, proprietaire_id: '' });
      return;
    }
    
    const proprietaire = (proprietaires || []).find(p => p.id === proprietaireId);
    if (!proprietaire) {
      setFormData({ ...formData, proprietaire_id: proprietaireId });
      return;
    }
    
    setFormData({
      ...formData,
      proprietaire_id: proprietaireId,
      numero_contact: proprietaire.telephone || formData.numero_contact,
      pays: proprietaire.pays || formData.pays,
    });
    
    setAutoFilledFields(prev => [...new Set([...prev, 'numero_contact', 'pays'])]);
  };
  
  const handleClientChange = (clientId) => {
    if (clientId === 'none') {
      setFormData({ ...formData, client_id: '', client_nom: '' });
      return;
    }
    
    const client = (clients || []).find(c => c.id === clientId);
    setFormData({ 
      ...formData, 
      client_id: clientId,
      client_nom: client ? client.nom : ''
    });
  };
  
  const calculateCoutImmobilisation = () => {
    const jours = parseInt(formData.jours_immobilisation) || 0;
    const tarif = parseFloat(formData.tarif_immobilisation_journalier) || 0;
    return jours * tarif;
  };

  // Base de données DISTANCES RÉELLES (données fournies par l'utilisateur)
  // Format: "origine|destination" -> distance en km
  const realDistances = {
    // Depuis Abidjan/Yopougon vers villes CI
    'Abidjan|Boundiali': 731,
    'Yopougon|Boundiali': 731,
    'Abidjan|Man': 564,
    'Yopougon|Man': 564,
    'Abidjan|Ferkessédougou': 573,
    'Yopougon|Ferkessédougou': 573,
    'Abidjan|Fekessedougou': 573,
    'Yopougon|Fekessedougou': 573,
    'Abidjan|Abengourou': 210,
    'Yopougon|Abengourou': 210,
    'Abidjan|Daloa': 377,
    'Yopougon|Daloa': 377,
    'Abidjan|Gagnoa': 271,
    'Yopougon|Gagnoa': 271,
    'Abidjan|Divo': 189,
    'Yopougon|Divo': 189,
    'Abidjan|Issia': 359,
    'Yopougon|Issia': 359,
    'Abidjan|Guiglo': 516,
    'Yopougon|Guiglo': 516,
    'Abidjan|Soubré': 367,
    'Yopougon|Soubré': 367,
    'Abidjan|San-Pédro': 368,
    'Yopougon|San-Pédro': 368,
    'Abidjan|Sassandra': 296,
    'Yopougon|Sassandra': 296,
    'Abidjan|Korhogo': 633,
    'Yopougon|Korhogo': 633,
    'Abidjan|Odienné': 729,
    'Yopougon|Odienné': 729,
    'Abidjan|Yamoussoukro': 243,
    'Yopougon|Yamoussoukro': 243,
    'Abidjan|YAKRO': 243,
    'Yopougon|YAKRO': 243,
    'Abidjan|Bouaké': 349,
    'Yopougon|Bouaké': 349,
    'Abidjan|Bouaflé': 302,
    'Yopougon|Bouaflé': 302,
    'Abidjan|Sinfra': 325,
    'Yopougon|Sinfra': 325,
    'Abidjan|Aboisso': 116,
    'Yopougon|Aboisso': 116,
    'Abidjan|Grand-Bassam': 43,
    'Yopougon|Grand-Bassam': 43,
    'Abidjan|BASSAM': 43,
    'Yopougon|BASSAM': 43,
    'Abidjan|Anyama': 22,
    'Yopougon|Anyama': 22,
    
    // ========================================
    // DISTANCES INTERNATIONALES - CEDEAO
    // ========================================
    
    // GHANA (Voisin Est)
    'Abidjan|Accra': 540, // Capitale Ghana - route côtière via frontière Elubo
    'Yopougon|Accra': 540,
    'Abidjan|Kumasi': 670, // 2e ville Ghana - via Accra
    'Yopougon|Kumasi': 670,
    'Abidjan|Tema': 560, // Port principal Ghana - proche Accra
    'Yopougon|Tema': 560,
    'Abidjan|Takoradi': 380, // Ville portuaire Ghana - route côtière
    'Yopougon|Takoradi': 380,
    
    // BURKINA FASO (Voisin Nord)
    'Abidjan|Ouagadougou': 1150, // Capitale Burkina - via Bobo-Dioulasso
    'Yopougon|Ouagadougou': 1150,
    'Abidjan|Bobo-Dioulasso': 870, // 2e ville Burkina - route principale
    'Yopougon|Bobo-Dioulasso': 870,
    
    // MALI (Nord-Ouest)
    'Abidjan|Bamako': 1200, // Capitale Mali - via Sikasso
    'Yopougon|Bamako': 1200,
    'Abidjan|Sikasso': 980, // Ville Sud Mali - frontière CI
    'Yopougon|Sikasso': 980,
    'Abidjan|Goulamina': 1100, // Mine de Goulamina
    
    // GUINÉE (Ouest)
    'Abidjan|Conakry': 990, // Capitale Guinée - via Man
    'Yopougon|Conakry': 990,
    'Abidjan|Beyla': 855, // Région forestière Guinée
    'Angré|Beyla': 855,
    'Abidjan|Kankan': 750, // Grande ville Est Guinée
    'Yopougon|Kankan': 750,
    'Abidjan|Nzérékoré': 680, // Ville Sud Guinée - frontière CI
    'Yopougon|Nzérékoré': 680,
    
    // LIBERIA (Sud-Ouest)
    'Abidjan|Monrovia': 720, // Capitale Liberia - route côtière via Harper
    'Yopougon|Monrovia': 720,
    'Abidjan|Harper': 520, // Ville Sud-Est Liberia - frontière CI
    'Yopougon|Harper': 520,
    
    // SÉNÉGAL (Nord-Ouest lointain)
    'Abidjan|Dakar': 2400, // Capitale Sénégal - via Mali/Guinée ou route côtière
    'Yopougon|Dakar': 2400,
    'Abidjan|Thiès': 2350, // Grande ville proche Dakar
    'Yopougon|Thiès': 2350,
    'Abidjan|Saint-Louis': 2550, // Ville Nord Sénégal
    'Yopougon|Saint-Louis': 2550,
    
    // TOGO (Est lointain)
    'Abidjan|Lomé': 750, // Capitale Togo - via Ghana (Accra)
    'Yopougon|Lomé': 750,
    'Abidjan|Kara': 950, // Ville Nord Togo
    'Yopougon|Kara': 950,
    
    // BÉNIN (Est lointain)
    'Abidjan|Cotonou': 950, // Capitale économique Bénin - via Lomé
    'Yopougon|Cotonou': 950,
    'Abidjan|Porto-Novo': 970, // Capitale politique Bénin
    'Yopougon|Porto-Novo': 970,
    'Abidjan|Parakou': 1150, // Grande ville Nord Bénin
    'Yopougon|Parakou': 1150,
    
    // NIGERIA (Est très lointain)
    'Abidjan|Lagos': 1050, // Capitale économique Nigeria - via Cotonou
    'Yopougon|Lagos': 1050,
    'Abidjan|Abuja': 1850, // Capitale politique Nigeria - centre pays
    'Yopougon|Abuja': 1850,
    'Abidjan|Ibadan': 1150, // 3e ville Nigeria
    'Yopougon|Ibadan': 1150,
    'Abidjan|Kano': 2200, // Grande ville Nord Nigeria
    'Yopougon|Kano': 2200,
    'Abidjan|Port Harcourt': 1450, // Ville pétrolière Sud Nigeria
    'Yopougon|Port Harcourt': 1450,
    
    // NIGER (Nord-Est très lointain)
    'Abidjan|Niamey': 1900, // Capitale Niger - via Burkina
    'Yopougon|Niamey': 1900,
    'Abidjan|Maradi': 2150, // Ville Sud Niger - proche Nigeria
    'Yopougon|Maradi': 2150,
    
    // GUINÉE-BISSAU (Ouest lointain)
    'Abidjan|Bissau': 1650, // Capitale Guinée-Bissau - via Conakry
    'Yopougon|Bissau': 1650,
    
    // SIERRA LEONE (Ouest)
    'Abidjan|Freetown': 1250, // Capitale Sierra Leone - via Conakry
    'Yopougon|Freetown': 1250,
    'Abidjan|Bo': 1100, // 2e ville Sierra Leone
    'Yopougon|Bo': 1100,
    
    // GAMBIE (Ouest très lointain)
    'Abidjan|Banjul': 2200, // Capitale Gambie - via Dakar
    'Yopougon|Banjul': 2200,
    
    // CAP-VERT (Îles - distance maritime)
    'Abidjan|Praia': 2800, // Capitale Cap-Vert - distance maritime + terrestre
    'Yopougon|Praia': 2800,
    
    // Distances intra-Abidjan (entre quartiers)
    'Yopougon|Treichville': 11,
    'Yopougon|Koumassi': 21,
    'Yopougon|Port-Bouët': 20,
    'Yopougon|Cocody': 11,
    'Yopougon|Abobo': 15,
    'Aéroport FHB|Treichville': 12,
    'Aéroport|Treichville': 12,
    
    // Distances symétriques (retour)
    'Boundiali|Abidjan': 731,
    'Boundiali|Yopougon': 731,
    'Man|Abidjan': 564,
    'Man|Yopougon': 564,
    'Ferkessédougou|Abidjan': 573,
    'Ferkessédougou|Yopougon': 573,
    'Fekessedougou|Abidjan': 573,
    'Fekessedougou|Yopougon': 573,
    'Abengourou|Abidjan': 210,
    'Abengourou|Yopougon': 210,
    'Daloa|Abidjan': 377,
    'Daloa|Yopougon': 377,
    'Gagnoa|Abidjan': 271,
    'Gagnoa|Yopougon': 271,
    'Divo|Abidjan': 189,
    'Divo|Yopougon': 189,
    'Issia|Abidjan': 359,
    'Issia|Yopougon': 359,
    'Guiglo|Abidjan': 516,
    'Guiglo|Yopougon': 516,
    'Soubré|Abidjan': 367,
    'Soubré|Yopougon': 367,
    'San-Pédro|Abidjan': 368,
    'San-Pédro|Yopougon': 368,
    'Sassandra|Abidjan': 296,
    'Sassandra|Yopougon': 296,
    'Korhogo|Abidjan': 633,
    'Korhogo|Yopougon': 633,
    'Odienné|Abidjan': 729,
    'Odienné|Yopougon': 729,
    'Yamoussoukro|Abidjan': 243,
    'Yamoussoukro|Yopougon': 243,
    'YAKRO|Abidjan': 243,
    'YAKRO|Yopougon': 243,
    'Bouaké|Abidjan': 349,
    'Bouaké|Yopougon': 349,
    'Bouaflé|Abidjan': 302,
    'Bouaflé|Yopougon': 302,
    'Sinfra|Abidjan': 325,
    'Sinfra|Yopougon': 325,
    'Aboisso|Abidjan': 116,
    'Aboisso|Yopougon': 116,
    'Grand-Bassam|Abidjan': 43,
    'Grand-Bassam|Yopougon': 43,
    'BASSAM|Abidjan': 43,
    'BASSAM|Yopougon': 43,
    'Anyama|Abidjan': 22,
    'Anyama|Yopougon': 22,
    'Goulamina|Abidjan': 1100,
    'Beyla|Abidjan': 855,
    'Beyla|Angré': 855,
    'Treichville|Yopougon': 11,
    'Koumassi|Yopougon': 21,
    'Port-Bouët|Yopougon': 20,
    'Cocody|Yopougon': 11,
    'Abobo|Yopougon': 15,
    'Treichville|Aéroport FHB': 12,
    'Treichville|Aéroport': 12,
    
    // ========================================
    // DISTANCES SYMÉTRIQUES - CEDEAO (Retours)
    // ========================================
    
    // Ghana (retours)
    'Accra|Abidjan': 540,
    'Accra|Yopougon': 540,
    'Kumasi|Abidjan': 670,
    'Kumasi|Yopougon': 670,
    'Tema|Abidjan': 560,
    'Tema|Yopougon': 560,
    'Takoradi|Abidjan': 380,
    'Takoradi|Yopougon': 380,
    
    // Burkina Faso (retours)
    'Ouagadougou|Abidjan': 1150,
    'Ouagadougou|Yopougon': 1150,
    'Bobo-Dioulasso|Abidjan': 870,
    'Bobo-Dioulasso|Yopougon': 870,
    
    // Mali (retours)
    'Bamako|Abidjan': 1200,
    'Bamako|Yopougon': 1200,
    'Sikasso|Abidjan': 980,
    'Sikasso|Yopougon': 980,
    'Goulamina|Abidjan': 1100,
    'Goulamina|Yopougon': 1100,
    
    // Guinée (retours)
    'Conakry|Abidjan': 990,
    'Conakry|Yopougon': 990,
    'Beyla|Abidjan': 855,
    'Beyla|Angré': 855,
    'Kankan|Abidjan': 750,
    'Kankan|Yopougon': 750,
    'Nzérékoré|Abidjan': 680,
    'Nzérékoré|Yopougon': 680,
    
    // Liberia (retours)
    'Monrovia|Abidjan': 720,
    'Monrovia|Yopougon': 720,
    'Harper|Abidjan': 520,
    'Harper|Yopougon': 520,
    
    // Sénégal (retours)
    'Dakar|Abidjan': 2400,
    'Dakar|Yopougon': 2400,
    'Thiès|Abidjan': 2350,
    'Thiès|Yopougon': 2350,
    'Saint-Louis|Abidjan': 2550,
    'Saint-Louis|Yopougon': 2550,
    
    // Togo (retours)
    'Lomé|Abidjan': 750,
    'Lomé|Yopougon': 750,
    'Kara|Abidjan': 950,
    'Kara|Yopougon': 950,
    
    // Bénin (retours)
    'Cotonou|Abidjan': 950,
    'Cotonou|Yopougon': 950,
    'Porto-Novo|Abidjan': 970,
    'Porto-Novo|Yopougon': 970,
    'Parakou|Abidjan': 1150,
    'Parakou|Yopougon': 1150,
    
    // Nigeria (retours)
    'Lagos|Abidjan': 1050,
    'Lagos|Yopougon': 1050,
    'Abuja|Abidjan': 1850,
    'Abuja|Yopougon': 1850,
    'Ibadan|Abidjan': 1150,
    'Ibadan|Yopougon': 1150,
    'Kano|Abidjan': 2200,
    'Kano|Yopougon': 2200,
    'Port Harcourt|Abidjan': 1450,
    'Port Harcourt|Yopougon': 1450,
    
    // Niger (retours)
    'Niamey|Abidjan': 1900,
    'Niamey|Yopougon': 1900,
    'Maradi|Abidjan': 2150,
    'Maradi|Yopougon': 2150,
    
    // Guinée-Bissau (retours)
    'Bissau|Abidjan': 1650,
    'Bissau|Yopougon': 1650,
    
    // Sierra Leone (retours)
    'Freetown|Abidjan': 1250,
    'Freetown|Yopougon': 1250,
    'Bo|Abidjan': 1100,
    'Bo|Yopougon': 1100,
    
    // Gambie (retours)
    'Banjul|Abidjan': 2200,
    'Banjul|Yopougon': 2200,
    
    // Cap-Vert (retours)
    'Praia|Abidjan': 2800,
    'Praia|Yopougon': 2800
  };
  
  // Base de données coordonnées (fallback si distance réelle non trouvée)
  const cityCoordinates = {
    // Côte d'Ivoire - Villes principales
    'Abidjan': { lat: 5.3600, lon: -4.0083 },
    'Yopougon': { lat: 5.3600, lon: -4.0083 },
    'Yamoussoukro': { lat: 6.8276, lon: -5.2893 },
    'YAKRO': { lat: 6.8276, lon: -5.2893 },
    'Bouaké': { lat: 7.6900, lon: -5.0300 },
    'Daloa': { lat: 6.8770, lon: -6.4503 },
    'San-Pédro': { lat: 4.7485, lon: -6.6363 },
    'Korhogo': { lat: 9.4580, lon: -5.6297 },
    'Man': { lat: 7.4125, lon: -7.5547 },
    'Gagnoa': { lat: 6.1319, lon: -5.9506 },
    // Côte d'Ivoire - Villes secondaires
    'Boundiali': { lat: 9.5200, lon: -6.4900 },
    'Ferkessédougou': { lat: 9.5981, lon: -5.1958 },
    'Fekessedougou': { lat: 9.5981, lon: -5.1958 },
    'Abengourou': { lat: 6.7294, lon: -3.4967 },
    'Divo': { lat: 5.8375, lon: -5.3569 },
    'Issia': { lat: 6.4925, lon: -6.5856 },
    'Guiglo': { lat: 6.5450, lon: -7.4936 },
    'Soubré': { lat: 5.7856, lon: -6.6092 },
    'Sassandra': { lat: 4.9519, lon: -6.0861 },
    'Odienné': { lat: 9.5097, lon: -7.5636 },
    'Bouaflé': { lat: 6.9897, lon: -5.7453 },
    'Sinfra': { lat: 6.6214, lon: -5.9097 },
    'Aboisso': { lat: 5.4714, lon: -3.2075 },
    'Grand-Bassam': { lat: 5.2111, lon: -3.7392 },
    'BASSAM': { lat: 5.2111, lon: -3.7392 },
    'Anyama': { lat: 5.4950, lon: -4.0517 },
    // Quartiers Abidjan
    'Treichville': { lat: 5.2936, lon: -4.0083 },
    'Koumassi': { lat: 5.2892, lon: -3.9472 },
    'Port-Bouët': { lat: 5.2656, lon: -3.9089 },
    'Cocody': { lat: 5.3472, lon: -3.9872 },
    'Abobo': { lat: 5.4178, lon: -4.0206 },
    'Angré': { lat: 5.3736, lon: -3.9750 },
    'Aéroport FHB': { lat: 5.2614, lon: -3.9264 },
    'Aéroport': { lat: 5.2614, lon: -3.9264 },
    // ========================================
    // GHANA
    // ========================================
    'Accra': { lat: 5.6037, lon: -0.1870 },
    'Kumasi': { lat: 6.6885, lon: -1.6244 },
    'Tema': { lat: 5.6698, lon: 0.0167 },
    'Takoradi': { lat: 4.8845, lon: -1.7554 },
    // ========================================
    // BURKINA FASO
    // ========================================
    'Ouagadougou': { lat: 12.3714, lon: -1.5197 },
    'Bobo-Dioulasso': { lat: 11.1770, lon: -4.2979 },
    // ========================================
    // MALI
    // ========================================
    'Bamako': { lat: 12.6392, lon: -8.0029 },
    'Sikasso': { lat: 11.3175, lon: -5.6667 },
    'Goulamina': { lat: 11.0500, lon: -8.5000 },
    // ========================================
    // SÉNÉGAL
    // ========================================
    'Dakar': { lat: 14.7167, lon: -17.4677 },
    'Thiès': { lat: 14.7886, lon: -16.9261 },
    'Saint-Louis': { lat: 16.0180, lon: -16.4897 },
    // ========================================
    // GUINÉE
    // ========================================
    'Conakry': { lat: 9.6412, lon: -13.5784 },
    'Beyla': { lat: 8.6833, lon: -8.6500 },
    'Kankan': { lat: 10.3856, lon: -9.3061 },
    'Nzérékoré': { lat: 7.7562, lon: -8.8179 },
    // ========================================
    // BÉNIN
    // ========================================
    'Cotonou': { lat: 6.3703, lon: 2.3912 },
    'Porto-Novo': { lat: 6.4969, lon: 2.6289 },
    'Parakou': { lat: 9.3372, lon: 2.6103 },
    // ========================================
    // TOGO
    // ========================================
    'Lomé': { lat: 6.1256, lon: 1.2223 },
    'Kara': { lat: 9.5511, lon: 1.1861 },
    // ========================================
    // NIGER
    // ========================================
    'Niamey': { lat: 13.5127, lon: 2.1128 },
    'Maradi': { lat: 13.5000, lon: 7.1017 },
    // ========================================
    // LIBERIA
    // ========================================
    'Monrovia': { lat: 6.3156, lon: -10.8074 },
    'Harper': { lat: 4.3750, lon: -7.7172 },
    
    // ========================================
    // SIERRA LEONE
    // ========================================
    'Freetown': { lat: 8.4657, lon: -13.2317 },
    'Bo': { lat: 7.9644, lon: -11.7383 },
    
    // ========================================
    // NIGERIA
    // ========================================
    'Lagos': { lat: 6.5244, lon: 3.3792 },
    'Abuja': { lat: 9.0765, lon: 7.3986 },
    'Ibadan': { lat: 7.3775, lon: 3.9470 },
    'Kano': { lat: 12.0022, lon: 8.5920 },
    'Port Harcourt': { lat: 4.8156, lon: 7.0498 },
    
    // ========================================
    // GUINÉE-BISSAU
    // ========================================
    'Bissau': { lat: 11.8637, lon: -15.5989 },
    
    // ========================================
    // GAMBIE
    // ========================================
    'Banjul': { lat: 13.4549, lon: -16.5790 },
    
    // ========================================
    // CAP-VERT
    // ========================================
    'Praia': { lat: 14.9330, lon: -23.5133 }
  };

  // Fonction pour calculer distance entre 2 points (formule de Haversine)
  const calculateDistanceBetweenCoordinates = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return Math.round(distance); // Arrondir à l'entier
  };

  // Fonction pour trouver les coordonnées d'une ville (recherche flexible)
  const findCityCoordinates = (cityName) => {
    if (!cityName) return null;
    
    // Nettoyer le nom de la ville
    const cleanName = cityName.trim();
    
    // Recherche exacte
    if (cityCoordinates[cleanName]) {
      return cityCoordinates[cleanName];
    }
    
    // Recherche insensible à la casse
    const lowerName = cleanName.toLowerCase();
    const foundKey = Object.keys(cityCoordinates).find(
      key => key.toLowerCase() === lowerName
    );
    
    if (foundKey) {
      return cityCoordinates[foundKey];
    }
    
    // Recherche partielle (commence par)
    const partialKey = Object.keys(cityCoordinates).find(
      key => key.toLowerCase().startsWith(lowerName) || lowerName.startsWith(key.toLowerCase())
    );
    
    return partialKey ? cityCoordinates[partialKey] : null;
  };

  // Fonction pour chercher distance réelle entre 2 villes
  const findRealDistance = (cityA, cityB) => {
    if (!cityA || !cityB) return null;
    
    // Nettoyer les noms de villes
    const cleanA = cityA.trim();
    const cleanB = cityB.trim();
    
    // Construire les clés possibles (sens A->B et B->A)
    const keys = [
      `${cleanA}|${cleanB}`,
      `${cleanB}|${cleanA}`,
      // Variations avec majuscules/minuscules
      `${cleanA.toLowerCase()}|${cleanB.toLowerCase()}`,
      `${cleanB.toLowerCase()}|${cleanA.toLowerCase()}`
    ];
    
    // Chercher dans les distances réelles
    for (const key of keys) {
      // Recherche exacte
      if (realDistances[key]) {
        return realDistances[key];
      }
      
      // Recherche insensible à la casse
      const foundKey = Object.keys(realDistances).find(
        k => k.toLowerCase() === key.toLowerCase()
      );
      if (foundKey) {
        return realDistances[foundKey];
      }
    }
    
    return null;
  };

  // Handler pour calculer distance automatiquement
  const calculateDistance = () => {
    // ÉTAPE 1: Chercher d'abord dans les distances réelles
    const realDistance = findRealDistance(formData.ville_depart, formData.ville_destination);
    
    if (realDistance) {
      setFormData({ ...formData, distance_km: realDistance.toString() });
      toast.success(
        `Distance routière: ${realDistance} km`,
        {
          description: `${formData.ville_depart} → ${formData.ville_destination} (distance réelle)`,
          duration: 5000
        }
      );
      return;
    }
    
    // ÉTAPE 2: Fallback sur calcul Haversine si distance réelle non trouvée
    const departCoords = findCityCoordinates(formData.ville_depart);
    const destCoords = findCityCoordinates(formData.ville_destination);
    
    if (!departCoords || !destCoords) {
      toast.error(
        `Impossible de calculer la distance. Ville non trouvée dans la base de données.`,
        {
          description: `Départ: ${formData.ville_depart} ${departCoords ? '✓' : '✗'}, Destination: ${formData.ville_destination} ${destCoords ? '✓' : '✗'}. Veuillez saisir manuellement.`
        }
      );
      return;
    }
    
    const distance = calculateDistanceBetweenCoordinates(
      departCoords.lat,
      departCoords.lon,
      destCoords.lat,
      destCoords.lon
    );
    
    setFormData({ ...formData, distance_km: distance.toString() });
    toast.warning(
      `Distance estimée: ${distance} km`,
      {
        description: `${formData.ville_depart} → ${formData.ville_destination} (à vol d'oiseau - estimation ±20%)`,
        duration: 5000
      }
    );
  };

  // Wizard steps configuration for operation form
  const operationWizardSteps = [
    {
      title: "Informations de base",
      description: "Date, opérateur, statut et client"
    },
    {
      title: "Transport",
      description: "Chauffeur, véhicule et capacité"
    },
    {
      title: "Trajet & Tarification",
      description: "Itinéraire et coûts"
    },
    {
      title: "Marchandise",
      description: "Type, poids et conteneur"
    },
    {
      title: "Dates Lifecycle",
      description: "Chargement et déchargement"
    },
    {
      title: "Immobilisation & Documents",
      description: "Coûts et documents"
    }
  ];

  // Wizard navigation handlers
  const handleWizardNext = () => {
    const errors = validateCurrentStep();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }
    setFormErrors({});
    setWizardStep(prev => Math.min(prev + 1, operationWizardSteps.length - 1));
  };

  const handleWizardPrev = () => {
    setWizardStep(prev => Math.max(prev - 1, 0));
  };

  const handleWizardSubmit = () => {
    // Trigger the form submit
    const form = document.getElementById('operation-form');
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  // Validation for each wizard step
  const validateCurrentStep = () => {
    const errors = {};
    
    switch (wizardStep) {
      case 0: // Informations de base
        if (!formData.date_operation) errors.date_operation = "La date est obligatoire";
        if (!formData.operateur_assigne) errors.operateur_assigne = "L'opérateur assigné est obligatoire";
        if (!formData.client_id) errors.client_id = "Le client est obligatoire";
        break;
      
      case 1: // Transport
        if (!formData.chauffeur_id) errors.chauffeur_id = "Le chauffeur est obligatoire";
        if (!formData.type_camion_id) errors.type_camion_id = "Le type de camion est obligatoire";
        if (!formData.capacite_t || parseFloat(formData.capacite_t) <= 0) errors.capacite_t = "La capacité du véhicule doit être supérieure à 0";
        break;
      
      case 3: // Marchandise
        if (!formData.type_marchandise) errors.type_marchandise = "Le type de marchandise est obligatoire";
        break;
      
      case 4: // Dates Lifecycle
        if (!editMode && !formData.datetime_chargement_prevu) {
          errors.datetime_chargement_prevu = "La date/heure de chargement prévu est obligatoire";
        }
        break;
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: datetime_chargement_prevu is mandatory for new operations
    if (!editMode && !formData.datetime_chargement_prevu) {
      toast.error('La date/heure de chargement prévu est obligatoire');
      return;
    }
    
    try {
      const data = {
        ...formData,
        nb_camions: parseInt(formData.nb_camions),
        capacite_t: parseFloat(formData.capacite_t),
        tarif_transport: formData.tarif_transport ? parseFloat(formData.tarif_transport) : null,
        tarif_transporteur: formData.tarif_transporteur ? parseFloat(formData.tarif_transporteur) : null,
        // tarif_client et marge_percent seront ajoutés uniquement lors du passage au statut "Facturé"
        jours_immobilisation: formData.jours_immobilisation ? parseInt(formData.jours_immobilisation) : null,
        tarif_immobilisation_journalier: formData.tarif_immobilisation_journalier ? parseFloat(formData.tarif_immobilisation_journalier) : null,
        // New lifecycle fields
        jours_immo: formData.jours_immo ? parseInt(formData.jours_immo) : null,
        tarif_unitaire_immo: formData.tarif_unitaire_immo ? parseFloat(formData.tarif_unitaire_immo) : null,
        poids_marchandise: formData.poids_marchandise ? parseFloat(formData.poids_marchandise) : null,
        distance_km: formData.distance_km ? parseFloat(formData.distance_km) : null,
        volume_marchandise: formData.volume_marchandise ? parseFloat(formData.volume_marchandise) : null,
      };
      
      // Remove file fields from data object
      delete data.bon_chargement_file;
      delete data.bon_dechargement_file;
      
      let operationId = formData.id;
      
      if (editMode) {
        // Update existing operation
        await api.put(`/operations/${operationId}`, data);
        toast.success('Opération modifiée avec succès');
      } else {
        // Create new operation
        const response = await api.post('/operations', data);
        operationId = response.data.id;
        toast.success('Opération créée avec succès');
      }
      
      // Upload documents if provided
      if (formData.bon_chargement_file) {
        toast.info('📤 Upload du bon de chargement...');
        const formDataChargement = new FormData();
        formDataChargement.append('file', formData.bon_chargement_file);
        formDataChargement.append('document_type', 'bon_chargement');
        formDataChargement.append('uploaded_by', 'admin');
        
        const uploadResult = await api.post(
          `/operations/${operationId}/documents`,
          formDataChargement,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        // Update operation with bon_chargement_id
        await api.put(`/operations/${operationId}`, {
          bon_chargement_id: uploadResult.data.document_id
        });
        
        toast.success('✅ Bon de chargement uploadé avec succès');
      }
      
      if (formData.bon_dechargement_file) {
        toast.info('📤 Upload du bon de déchargement...');
        const formDataDechargement = new FormData();
        formDataDechargement.append('file', formData.bon_dechargement_file);
        formDataDechargement.append('document_type', 'bon_dechargement');
        formDataDechargement.append('uploaded_by', 'admin');
        
        const uploadResult = await api.post(
          `/operations/${operationId}/documents`,
          formDataDechargement,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        // Update operation with bon_dechargement_id
        await api.put(`/operations/${operationId}`, {
          bon_dechargement_id: uploadResult.data.document_id
        });
        
        toast.success('✅ Bon de déchargement uploadé avec succès');
      }
      
      setDialogOpen(false);
      resetForm();
      setAutoFilledFields([]);
      setEditMode(false);
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        if (errorDetail.message) {
          toast.error(errorDetail.message);
        } else if (Array.isArray(errorDetail)) {
          // Pydantic validation errors array
          const messages = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(messages);
        } else {
          // Single Pydantic error object
          toast.error(errorDetail.msg || JSON.stringify(errorDetail));
        }
      } else {
        toast.error(errorDetail || 'Erreur lors de l\'opération');
      }
    }
  };

  const handleEditOperation = (operation) => {
    // Helper function to convert ISO datetime to datetime-local format
    const toDatetimeLocal = (isoString) => {
      if (!isoString) return '';
      try {
        // Remove 'Z' and milliseconds if present, keep YYYY-MM-DDTHH:mm format
        return isoString.slice(0, 16);
      } catch {
        return '';
      }
    };

    setFormData({
      id: operation.id,
      date_operation: operation.date_operation || '',
      transporteur: operation.transporteur || '',
      operateur: operation.operateur || '',
      type_camion_id: operation.type_camion_id || '',
      nb_camions: operation.nb_camions?.toString() || '1',
      capacite_t: operation.capacite_t?.toString() || '',
      ville_depart: operation.ville_depart || '',
      ville_destination: operation.ville_destination || '',
      tarif_transport: operation.tarif_transport?.toString() || '',
      tarif_transporteur: operation.tarif_transporteur?.toString() || '',
      // tarif_client et marge_percent ne sont plus dans le wizard
      jours_immobilisation: operation.jours_immobilisation?.toString() || '',
      tarif_immobilisation_journalier: operation.tarif_immobilisation_journalier?.toString() || '',
      commentaire_operateur: operation.commentaire_operateur || '',
      semi_remorque: operation.semi_remorque || false,
      tracteur: operation.tracteur || '',
      observations: operation.observations || '',
      statut: operation.statut || 'En cours',
      vehicule_id: operation.vehicule_id || '',
      vehicle_id: operation.vehicle_id || '',
      chauffeur_id: operation.chauffeur_id || '',
      proprietaire_id: operation.proprietaire_id || '',
      client_id: operation.client_id || '',
      numero_contact: operation.numero_contact || '',
      pays: operation.pays || 'Côte d\'Ivoire',
      operateur_assigne: operation.operateur_assigne || '',
      intra_inter_land: operation.intra_inter_land || 'Intraland',
      // Nouveaux champs ajoutés
      client_nom: operation.client_nom || '',
      descriptif_operation: operation.descriptif_operation || '',
      type_marchandise: operation.type_marchandise || '',
      poids_marchandise: operation.poids_marchandise?.toString() || '',
      volume_marchandise: operation.volume_marchandise?.toString() || '',
      distance_km: operation.distance_km?.toString() || '',
      est_conteneur: operation.est_conteneur || false,
      type_conteneur: operation.type_conteneur || '',
      is_multi_destination: operation.is_multi_destination || false,
      // Dates avec conversion pour datetime-local inputs
      datetime_chargement_prevu: toDatetimeLocal(operation.datetime_chargement_prevu),
      datetime_chargement_reel: toDatetimeLocal(operation.datetime_chargement_reel),
      datetime_dechargement_prevu: toDatetimeLocal(operation.datetime_dechargement_prevu),
      datetime_dechargement_reel: toDatetimeLocal(operation.datetime_dechargement_reel),
      jours_immo: operation.jours_immo?.toString() || '',
      tarif_unitaire_immo: operation.tarif_unitaire_immo?.toString() || '',
      bon_chargement_id: operation.bon_chargement_id || '',
      bon_dechargement_id: operation.bon_dechargement_id || '',
      bon_chargement_file: null,
      bon_dechargement_file: null,
      etapes: operation.etapes || [], // Charger les étapes existantes
    });
    
    // Charger les étapes intermédiaires si présentes
    if (operation.etapes && Array.isArray(operation.etapes) && operation.etapes.length > 0) {
      setIntermediateSteps(operation.etapes.sort((a, b) => a.ordre - b.ordre));
    } else {
      setIntermediateSteps([]);
    }
    
    // Initialiser les locations pour le calcul de distance
    // Note: On ne peut pas initialiser complètement originLocation et destinationLocation
    // car on n'a que les noms de villes, pas les coordonnées exactes de l'autocomplete
    // Donc on laisse l'utilisateur re-sélectionner si besoin de recalculer la distance
    setOriginLocation(null);
    setDestinationLocation(null);
    setCalculatedDistance(null);
    
    setCurrentOperationId(operation.id);
    setEditMode(true);
    setWizardStep(0); // Réinitialiser à la première étape du wizard
    setDialogOpen(true);
    // Charger les documents
    if (operation.id) {
      fetchDocuments(operation.id);
    }
  };

  // Documents functions
  const fetchDocuments = async (operationId) => {
    try {
      const response = await api.get(`/operations/${operationId}/documents`);
      setDocuments(response.data.documents || []);
      setDocumentsStats(response.data.stats || null);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleDocumentUpload = async (e, documentType) => {
    const file = e.target.files?.[0];
    if (!file || !currentOperationId) return;

    // Validation
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez: PDF, JPG, PNG');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 MB)');
      return;
    }

    try {
      setUploadingDocument(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', documentType);

      await api.post(`/operations/${currentOperationId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploadé avec succès');
      fetchDocuments(currentOperationId);
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingDocument(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleDocumentDelete = async (documentId) => {
    if (!confirm('Supprimer ce document ?')) return;

    try {
      await api.delete(`/operations/${currentOperationId}/documents/${documentId}`);
      toast.success('Document supprimé');
      fetchDocuments(currentOperationId);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDocumentDownload = (documentId, filename) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/operations/${currentOperationId}/documents/${documentId}/download`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  // Bulk selection handlers
  const handleSelectAll = () => {
    // Sélectionner uniquement les éléments de la page actuelle
    const currentPageIds = paginatedOperations.map(o => o.id);
    setSelectedIds(currentPageIds);
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await api.post('/operations/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} opération(s) supprimée(s)`);
      setSelectedIds([]);
      setDeleteConfirmOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  const handleBulkAssign = async (userId) => {
    try {
      await Promise.all(selectedIds.map(id => 
        api.put(`/operations/${id}`, { operateur_assigne: userId })
      ));
      toast.success(`${selectedIds.length} opération(s) assignée(s)`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'assignement');
    }
  };
  
  const handleBulkStatusChange = async (newStatus) => {
    try {
      await Promise.all(selectedIds.map(id => 
        api.put(`/operations/${id}/status?statut=${newStatus}`)
      ));
      toast.success(`${selectedIds.length} opération(s) passée(s) à "${newStatus}"`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const resetForm = () => {
    setFormData({
      date_operation: new Date().toISOString().split('T')[0],
      operateur: '',
      operateur_assigne: '',
      statut: 'Nouveau',
      chauffeur_id: '',
      proprietaire_id: '',
      client_id: '',
      numero_contact: '',
      type_camion_id: '',
      nb_camions: 1,
      capacite_t: '',
      semi_remorque: false,
      tracteur: '',
      pays: 'Côte d\'Ivoire',
      intra_inter_land: 'Intraland',
      ville_depart: '',
      ville_destination: '',
      distance_km: '',
      tarif_transport: '',
      tarif_transporteur: '',
      transporteur: '',
      jours_immobilisation: '',
      tarif_immobilisation_journalier: '',
      commentaire_operateur: '',
      client_nom: '',
      descriptif_operation: '',
      type_marchandise: '',
      poids_marchandise: '',
      volume_marchandise: '',
      est_conteneur: false,
      type_conteneur: '',
      datetime_chargement_prevu: '',
      datetime_chargement_reel: '',
      datetime_dechargement_prevu: '',
      datetime_dechargement_reel: '',
      jours_immo: '',
      tarif_unitaire_immo: '',
      bon_chargement_file: null,
      bon_dechargement_file: null,
    });
    setWizardStep(0);
    setFormErrors({});
    setIntermediateSteps([]);
    setOriginLocation(null);
    setDestinationLocation(null);
    setCalculatedDistance(null);
  };

  const getChauffeurNom = (id) => {
    const c = (chauffeurs || []).find((ch) => ch.id === id);
    return c ? c.nom : 'N/A';
  };

  const getProprietaireNom = (id) => {
    const p = (proprietaires || []).find((pr) => pr.id === id);
    return p ? p.nom : 'N/A';
  };

  const getOperateurNom = (id) => {
    if (!id) return '-';
    const user = (users || []).find((u) => u.id === id);
    return user ? `${user.nom || ''} ${user.prenom || ''}`.trim() || user.email : 'N/A';
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filtering and sorting logic
  const getFilteredAndSortedOperations = () => {
    let filtered = [...operations];

    // Apply filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(op => 
        (op.matricule?.toLowerCase().includes(searchLower)) ||
        (op.transporteur?.toLowerCase().includes(searchLower)) ||
        (op.ville_depart?.toLowerCase().includes(searchLower)) ||
        (op.ville_destination?.toLowerCase().includes(searchLower)) ||
        (op.numero_bl?.toLowerCase().includes(searchLower))
      );
    }

    if (filters.statut !== 'all') {
      filtered = filtered.filter(op => op.statut === filters.statut);
    }

    if (filters.chauffeur !== 'all') {
      filtered = filtered.filter(op => op.chauffeur_id === filters.chauffeur);
    }

    if (filters.transporteur !== 'all') {
      filtered = filtered.filter(op => op.transporteur === filters.transporteur);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(op => op.date_operation >= filters.dateFrom);
    }

    if (filters.dateTo) {
      filtered = filtered.filter(op => op.date_operation <= filters.dateTo);
    }

    // Filter by client
    if (filters.client !== 'all') {
      filtered = filtered.filter(op => op.client_nom === filters.client);
    }

    // Filter by immatriculation (vehicle plate)
    if (filters.immatriculation) {
      const immatLower = filters.immatriculation.toLowerCase();
      filtered = filtered.filter(op => {
        if (!op.vehicle_snapshot) return false;
        return op.vehicle_snapshot.immatriculation?.toLowerCase().includes(immatLower);
      });
    }

    // Filter by date chargement
    if (filters.dateChargementFrom) {
      filtered = filtered.filter(op => {
        if (!op.datetime_chargement_prevu) return false;
        return op.datetime_chargement_prevu >= filters.dateChargementFrom;
      });
    }

    if (filters.dateChargementTo) {
      filtered = filtered.filter(op => {
        if (!op.datetime_chargement_prevu) return false;
        return op.datetime_chargement_prevu <= filters.dateChargementTo;
      });
    }

    // Filter by date dechargement
    if (filters.dateDechargementFrom) {
      filtered = filtered.filter(op => {
        if (!op.datetime_dechargement_prevu) return false;
        return op.datetime_dechargement_prevu >= filters.dateDechargementFrom;
      });
    }

    if (filters.dateDechargementTo) {
      filtered = filtered.filter(op => {
        if (!op.datetime_dechargement_prevu) return false;
        return op.datetime_dechargement_prevu <= filters.dateDechargementTo;
      });
    }

    // Filter by type_vehicule (type de camion)
    if (filters.type_vehicule !== 'all') {
      filtered = filtered.filter(op => op.type_camion_id === filters.type_vehicule);
    }

    // Filter by type_marchandise
    if (filters.type_marchandise) {
      const marchandiseLower = filters.type_marchandise.toLowerCase();
      filtered = filtered.filter(op => 
        op.type_marchandise?.toLowerCase().includes(marchandiseLower)
      );
    }

    // Filter by ville_depart
    if (filters.ville_depart) {
      const villeDepartLower = filters.ville_depart.toLowerCase();
      filtered = filtered.filter(op => 
        op.ville_depart?.toLowerCase().includes(villeDepartLower)
      );
    }

    // Filter by ville_destination
    if (filters.ville_destination) {
      const villeDestLower = filters.ville_destination.toLowerCase();
      filtered = filtered.filter(op => 
        op.ville_destination?.toLowerCase().includes(villeDestLower)
      );
    }

    // Filter by is_multi_destination
    if (filters.is_multi_destination !== 'all') {
      const isMulti = filters.is_multi_destination === 'true';
      filtered = filtered.filter(op => op.is_multi_destination === isMulti);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle nested values
      if (sortConfig.key === 'chauffeur') {
        aVal = getChauffeurNom(a.chauffeur_id);
        bVal = getChauffeurNom(b.chauffeur_id);
      }

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Compare
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const filteredOperations = getFilteredAndSortedOperations();

  // Pagination logic with useMemo
  const totalPages = useMemo(() => Math.ceil(filteredOperations.length / pageSize), [filteredOperations.length, pageSize]);
  const paginatedOperations = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredOperations.slice(startIndex, endIndex);
  }, [filteredOperations, currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedIds([]);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setSelectedIds([]);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Get unique values for filter dropdowns
  const uniqueTransporteurs = [...new Set((operations || []).map(op => op.transporteur).filter(Boolean))];

  // Sortable column header component
  const SortableHeader = ({ sortKey, label }) => {
    const isSorted = sortConfig.key === sortKey;
    const direction = sortConfig.direction;

    return (
      <th 
        className="text-left py-3 px-3 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
        onClick={() => handleSort(sortKey)}
      >
        <div className="flex items-center gap-2">
          {label}
          {isSorted ? (
            direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </th>
    );
  };

  const getTypeCamionLibelle = (id) => {
    const t = (typesCamion || []).find((tc) => tc.id === id);
    return t ? t.libelle : 'N/A';
  };
  
  const getVehiculeLabel = (id) => {
    const vehicule = (vehicules || []).find(v => v.id === id);
    if (!vehicule) return 'Inconnu';
    return `${vehicule.immatriculation} - ${getTypeCamionLibelle(vehicule.type_camion_id)}`;
  };
  
  const getUserNom = (id) => {
    const user = (users || []).find(u => u.id === id);
    return user ? user.nom : 'Non assigné';
  };
  
  const getStatutBadgeColor = (statut) => {
    switch (statut) {
      case 'Nouveau':
        return 'bg-purple-100 text-purple-800';
      case 'En cours':
        return 'bg-blue-100 text-blue-800';
      case 'Terminée':
        return 'bg-green-100 text-green-800';
      case 'Reporté':
        return 'bg-yellow-100 text-yellow-800';
      case 'Annulée':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Validation par étape du wizard de clôture
  const canProceedClosureStep = () => {
    switch (closureWizardStep) {
      case 0: // Dates & Heures
        return closureData.date_depart_reel && 
               closureData.heure_depart_reel && 
               closureData.date_arrivee_reel && 
               closureData.heure_arrivee_reel;
      case 1: // Documents
        // Documents non obligatoires, permettre de continuer
        return true;
      case 2: // Incidents
        // Si incident déclaré, la description est obligatoire
        if (closureData.incidents_rencontres && showIncidentForm) {
          return incidentData.description.trim() !== '';
        }
        return true;
      case 3: // Récapitulatif
        return true;
      default:
        return false;
    }
  };

  const handleClosureNext = () => {
    if (canProceedClosureStep()) {
      setClosureWizardStep(prev => prev + 1);
    }
  };

  const handleClosurePrev = () => {
    setClosureWizardStep(prev => prev - 1);
  };
  
  const handleStatusChange = async (operationId, newStatus) => {
    // Si on veut terminer l'opération, ouvrir le modal de clôture
    if (newStatus === 'Terminée') {
      const operation = operations.find(op => op.id === operationId);
      setOperationToClose(operation);
      setClosureDialogOpen(true);
      return;
    }
    
    // Si on veut passer au statut "Facturé", ouvrir le modal de tarification client
    if (newStatus === 'Facturé') {
      const operation = operations.find(op => op.id === operationId);
      setOperationToBill(operation);
      setBillingDialogOpen(true);
      return;
    }
    
    // Pour les autres statuts, mise à jour directe
    try {
      await api.put(`/operations/${operationId}/status?statut=${newStatus}`);
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        if (errorDetail.message) {
          toast.error(errorDetail.message);
        } else if (Array.isArray(errorDetail)) {
          // Pydantic validation errors array
          const messages = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(messages);
        } else {
          // Single Pydantic error object
          toast.error(errorDetail.msg || JSON.stringify(errorDetail));
        }
      } else {
        toast.error(errorDetail || 'Erreur lors de la mise à jour du statut');
      }
    }
  };

  const handleBillingSubmit = async () => {
    try {
      if (!billingData.tarif_client) {
        toast.error('Veuillez saisir le tarif client');
        return;
      }
      
      await api.put(`/operations/${operationToBill.id}`, {
        tarif_client: parseFloat(billingData.tarif_client),
        marge_percent: parseFloat(billingData.marge_percent),
        statut: 'Facturé'
      });
      
      toast.success('Opération facturée avec succès');
      setBillingDialogOpen(false);
      setBillingData({ tarif_transporteur: '', marge_percent: '25', tarif_client: '' });
      setOperationToBill(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la facturation');
    }
  };

  const handleDuplicateOperation = async (operationId) => {
    try {
      const response = await api.post(`/operations/${operationId}/duplicate`);
      toast.success(`Opération dupliquée: ${response.data.matricule}`);
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(errorDetail?.message || errorDetail || 'Erreur lors de la duplication');
    }
  };

  const handleDeleteSingleOperation = async (operationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette opération ?')) {
      return;
    }
    
    try {
      await api.post('/operations/bulk-delete', { ids: [operationId] });
      toast.success('Opération supprimée');
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(errorDetail?.message || errorDetail || 'Erreur lors de la suppression');
    }
  };

  const handleClosureSubmit = async () => {
    if (!operationToClose) return;
    
    // Validation des champs obligatoires
    if (!closureData.date_depart_reel || !closureData.heure_depart_reel) {
      toast.error("La date et l'heure de départ réelle sont obligatoires");
      return;
    }
    if (!closureData.date_arrivee_reel || !closureData.heure_arrivee_reel) {
      toast.error("La date et l'heure d'arrivée réelle sont obligatoires");
      return;
    }
    
    // Vérifier si les documents manquent et afficher un rappel
    const missingDocs = [];
    if (!closureData.bon_chargement_file) missingDocs.push("bon de chargement");
    if (!closureData.bon_dechargement_file) missingDocs.push("bon de déchargement");
    
    if (missingDocs.length > 0) {
      const confirmClosure = window.confirm(
        `⚠️ RAPPEL IMPORTANT\n\n` +
        `Les documents suivants sont manquants :\n` +
        `${missingDocs.map(d => `• ${d}`).join('\n')}\n\n` +
        `Il est fortement recommandé de téléverser ces documents dans l'onglet "Documents".\n\n` +
        `Voulez-vous terminer l'opération sans ces documents ?`
      );
      
      if (!confirmClosure) {
        toast.info("❌ Clôture annulée. Veuillez ajouter les documents manquants.");
        return;
      }
    }
    
    // Validation: Si incident déclaré, la description est obligatoire
    if (closureData.incidents_rencontres && showIncidentForm && !incidentData.description.trim()) {
      toast.error("La description de l'incident est obligatoire");
      return;
    }
    
    // Combiner date et heure en datetime ISO
    const datetimeChargementReel = `${closureData.date_depart_reel}T${closureData.heure_depart_reel}:00`;
    const datetimeDechargementReel = `${closureData.date_arrivee_reel}T${closureData.heure_arrivee_reel}:00`;
    
    try {
      let bonChargementId = null;
      let bonDechargementId = null;
      
      // Upload bon de chargement uniquement si fourni
      if (closureData.bon_chargement_file) {
        toast.info('📤 Upload du bon de chargement...');
        const formDataChargement = new FormData();
        formDataChargement.append('file', closureData.bon_chargement_file);
        formDataChargement.append('document_type', 'bon_chargement');
        formDataChargement.append('uploaded_by', 'admin');
        
        const uploadChargement = await api.post(
          `/operations/${operationToClose.id}/documents`,
          formDataChargement,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        bonChargementId = uploadChargement.data.document_id;
        toast.success('✅ Bon de chargement uploadé');
      }
      
      // Upload bon de déchargement uniquement si fourni
      if (closureData.bon_dechargement_file) {
        toast.info('📤 Upload du bon de déchargement...');
        const formDataDechargement = new FormData();
        formDataDechargement.append('file', closureData.bon_dechargement_file);
        formDataDechargement.append('document_type', 'bon_dechargement');
        formDataDechargement.append('uploaded_by', 'admin');
        
        const uploadDechargement = await api.post(
          `/operations/${operationToClose.id}/documents`,
          formDataDechargement,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        bonDechargementId = uploadDechargement.data.document_id;
        toast.success('✅ Bon de déchargement uploadé');
      }
      
      // Mettre à jour l'opération avec les dates réelles et le statut
      toast.info('🔄 Mise à jour de l\'opération...');
      const updatePayload = {
        datetime_chargement_reel: datetimeChargementReel,
        datetime_dechargement_reel: datetimeDechargementReel,
        statut: 'Terminée'
      };
      
      // Ajouter les IDs de documents seulement s'ils ont été uploadés
      if (bonChargementId) updatePayload.bon_chargement_id = bonChargementId;
      if (bonDechargementId) updatePayload.bon_dechargement_id = bonDechargementId;
      
      await api.put(`/operations/${operationToClose.id}`, updatePayload);
      toast.success('✅ Opération clôturée avec succès');
      
      // Afficher un rappel si des documents manquent
      if (!bonChargementId || !bonDechargementId) {
        setTimeout(() => {
          toast.warning('⚠️ N\'oubliez pas d\'ajouter les documents manquants dans l\'onglet "Documents"');
        }, 1500);
      }
      
      // Si un incident a été déclaré, le créer
      if (closureData.incidents_rencontres && showIncidentForm) {
        try {
          toast.info('Création de l\'incident...');
          
          // Préparer les données de l'incident avec héritage automatique
          const incidentPayload = {
            operation_id: operationToClose.id,
            chauffeur_id: operationToClose.chauffeur_id,
            type_incident: incidentData.type_incident,
            gravite: incidentData.gravite,
            description: incidentData.description,
            date_incident: new Date().toISOString(),
            statut: 'Ouvert'
          };
          
          // Créer l'incident
          const incidentResponse = await api.post('/incidents', incidentPayload);
          const incidentId = incidentResponse.data.id;
          
          // Upload de la pièce jointe si présente
          if (incidentData.piece_jointe) {
            const formDataIncident = new FormData();
            formDataIncident.append('file', incidentData.piece_jointe);
            formDataIncident.append('incident_id', incidentId);
            formDataIncident.append('uploaded_by', 'admin');
            
            await api.post('/incidents/upload', formDataIncident, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
          
          toast.success('Incident créé et lié à l\'opération');
        } catch (incidentError) {
          console.error('Erreur création incident:', incidentError);
          toast.warning('Opération clôturée mais erreur lors de la création de l\'incident');
        }
      }
      
      toast.success('Opération clôturée avec succès');
      setClosureDialogOpen(false);
      setOperationToClose(null);
      setClosureWizardStep(0);
      setClosureData({
        date_depart_reel: '',
        heure_depart_reel: '',
        date_arrivee_reel: '',
        heure_arrivee_reel: '',
        incidents_rencontres: false,
        bon_chargement_file: null,
        bon_dechargement_file: null
      });
      setIncidentData({
        type_incident: 'Panne',
        gravite: 'Moyenne',
        description: '',
        piece_jointe: null
      });
      setShowIncidentForm(false);
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        if (errorDetail.message) {
          toast.error(errorDetail.message);
        } else if (Array.isArray(errorDetail)) {
          // Pydantic validation errors array
          const messages = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(messages);
        } else {
          // Single Pydantic error object
          toast.error(errorDetail.msg || JSON.stringify(errorDetail));
        }
      } else {
        toast.error(errorDetail || "Erreur lors de la clôture de l'opération");
      }
    }
  };
  
  const isFieldAutoFilled = (fieldName) => autoFilledFields.includes(fieldName);

  // Composant pour afficher les documents du chauffeur
  const DocumentsChauffeurSection = ({ chauffeurId }) => {
    const [chauffeurDocs, setChauffeurDocs] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchChauffeurDocs = async () => {
        try {
          const response = await api.get(`/chauffeurs/${chauffeurId}`);
          setChauffeurDocs(response.data);
        } catch (error) {
          console.error('Error fetching chauffeur docs:', error);
        } finally {
          setLoading(false);
        }
      };
      if (chauffeurId) fetchChauffeurDocs();
    }, [chauffeurId]);

    if (loading) return <p className="text-sm text-gray-500">Chargement...</p>;
    if (!chauffeurDocs) return <p className="text-sm text-gray-500">Chauffeur non trouvé</p>;

    const permis = chauffeurDocs.documents_permis || [];
    const cni = chauffeurDocs.documents_cni || [];

    const downloadFile = async (url, filename) => {
      try {
        const response = await api.get(url, { responseType: 'blob' });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Document téléchargé');
      } catch (error) {
        toast.error('Erreur lors du téléchargement');
      }
    };

    return (
      <div className="space-y-3">
        {/* Permis de conduire */}
        {permis.length > 0 ? (
          permis.map((doc, idx) => (
            <div key={`permis-${idx}`} className="flex items-center justify-between p-2 bg-white rounded border">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded bg-amber-100 text-amber-600">
                  <FileText className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">Permis de conduire</p>
                  <p className="text-xs text-gray-500">N° {doc.numero} • Expire: {doc.date_expiration}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  doc.statut === 'Valide' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>{doc.statut}</span>
              </div>
              {doc.fichier_url && (
                <Button variant="ghost" size="sm" onClick={() => downloadFile(doc.fichier_url, `permis_${doc.numero}.pdf`)}>
                  <Download className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-500">Aucun permis enregistré</p>
        )}

        {/* CNI */}
        {cni.length > 0 ? (
          cni.map((doc, idx) => (
            <div key={`cni-${idx}`} className="flex items-center justify-between p-2 bg-white rounded border">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded bg-purple-100 text-purple-600">
                  <FileText className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">Carte d'identité</p>
                  <p className="text-xs text-gray-500">N° {doc.numero} • Expire: {doc.date_expiration}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  doc.statut === 'Valide' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>{doc.statut}</span>
              </div>
              {doc.fichier_url && (
                <Button variant="ghost" size="sm" onClick={() => downloadFile(doc.fichier_url, `cni_${doc.numero}.pdf`)}>
                  <Download className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-500">Aucune CNI enregistrée</p>
        )}
      </div>
    );
  };

  // Composant pour afficher les documents du véhicule
  const DocumentsVehiculeSection = ({ vehiculeId }) => {
    const [vehiculeDocs, setVehiculeDocs] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchVehiculeDocs = async () => {
        try {
          const response = await api.get(`/vehicules/${vehiculeId}`);
          setVehiculeDocs(response.data);
        } catch (error) {
          console.error('Error fetching vehicule docs:', error);
        } finally {
          setLoading(false);
        }
      };
      if (vehiculeId) fetchVehiculeDocs();
    }, [vehiculeId]);

    if (loading) return <p className="text-sm text-gray-500">Chargement...</p>;
    if (!vehiculeDocs) return <p className="text-sm text-gray-500">Véhicule non trouvé</p>;

    const downloadFile = async (url, filename) => {
      try {
        // Handle both relative API URLs and direct file URLs
        const apiUrl = url.startsWith('/api') ? url : `/api${url}`;
        const response = await api.get(apiUrl, { responseType: 'blob' });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Document téléchargé');
      } catch (error) {
        toast.error('Erreur lors du téléchargement');
      }
    };

    const carteGrise = vehiculeDocs.carte_grise;
    const assurance = vehiculeDocs.assurance;
    const photo = vehiculeDocs.photo_url;

    return (
      <div className="space-y-3">
        {/* Carte grise */}
        {carteGrise?.fichier_url ? (
          <div className="flex items-center justify-between p-2 bg-white rounded border">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-emerald-100 text-emerald-600">
                <FileText className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium">Carte grise</p>
                <p className="text-xs text-gray-500">
                  N° {carteGrise.numero_document || vehiculeDocs.immatriculation} • 
                  Expire: {carteGrise.date_expiration || 'N/A'}
                </p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${
                carteGrise.statut === 'Valide' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>{carteGrise.statut || 'N/A'}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => downloadFile(carteGrise.fichier_url, `carte_grise_${vehiculeDocs.immatriculation}.pdf`)}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <p className="text-xs text-gray-500">Aucune carte grise enregistrée</p>
        )}

        {/* Assurance */}
        {assurance?.fichier_url ? (
          <div className="flex items-center justify-between p-2 bg-white rounded border">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-blue-100 text-blue-600">
                <FileText className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium">Assurance</p>
                <p className="text-xs text-gray-500">
                  N° {assurance.numero_document || 'N/A'} • 
                  Expire: {assurance.date_expiration || 'N/A'}
                </p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${
                assurance.statut === 'Valide' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>{assurance.statut || 'N/A'}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => downloadFile(assurance.fichier_url, `assurance_${vehiculeDocs.immatriculation}.pdf`)}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <p className="text-xs text-gray-500">Aucune assurance enregistrée</p>
        )}

        {/* Photo du véhicule */}
        {photo && (
          <div className="flex items-center justify-between p-2 bg-white rounded border">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-gray-100 text-gray-600">
                <ImageIcon className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium">Photo du véhicule</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => downloadFile(photo, `photo_${vehiculeDocs.immatriculation}.jpg`)}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Render wizard step content for operation creation
  const renderWizardStepContent = () => {
    switch (wizardStep) {
      case 0: // Informations de base
        return (
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Date*</Label>
              <Input 
                type="date" 
                value={formData.date_operation} 
                onChange={(e) => setFormData({ ...formData, date_operation: e.target.value })} 
                required 
                data-testid="op-date-input" 
              />
            </div>
            
            <div className="space-y-2">
              <LabelWithTooltip 
                label="Opérateur assigné*" 
                tooltip="Utilisateur responsable de la gestion et du suivi de cette opération"
              />
              <Select 
                value={formData.operateur_assigne || 'none'} 
                onValueChange={(value) => {
                  const userName = value === 'none' ? '' : (users || []).find(u => u.id === value)?.nom || '';
                  setFormData({ 
                    ...formData, 
                    operateur_assigne: value === 'none' ? '' : value,
                    operateur: userName
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un opérateur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {(users || []).filter(u => u.role === 'Operateur' || u.role === 'Admin').map((user) => (
                    <SelectItem key={user.id} value={user.id}>{user.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Statut*</Label>
              <Select value={formData.statut} onValueChange={(value) => setFormData({ ...formData, statut: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Nouveau">Nouveau</SelectItem>
                  <SelectItem value="En attente">En attente</SelectItem>
                  <SelectItem value="En cours">En cours</SelectItem>
                  <SelectItem value="En transit">En transit</SelectItem>
                  <SelectItem value="En chargement">En chargement</SelectItem>
                  <SelectItem value="En déchargement">En déchargement</SelectItem>
                  <SelectItem value="Chargé">Chargé</SelectItem>
                  <SelectItem value="Validé">Validé</SelectItem>
                  <SelectItem value="Terminée">Terminée</SelectItem>
                  <SelectItem value="Facturé">Facturé</SelectItem>
                  <SelectItem value="Reporté">Reporté</SelectItem>
                  <SelectItem value="Annulée">Annulée</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Client (Obligatoire)</Label>
              <Combobox
                options={[
                  { value: "none", label: "Sélectionner un client" },
                  ...(clients || []).map((cl) => ({
                    value: cl.id,
                    label: `${cl.matricule} - ${cl.nom}`,
                    searchText: `${cl.matricule} ${cl.nom} ${cl.nom_contact || ''} ${cl.ville || ''}`.toLowerCase()
                  }))
                ]}
                value={formData.client_id || "none"}
                onValueChange={handleClientChange}
                placeholder="Rechercher un client"
                searchPlaceholder="Matricule, nom, contact..."
                emptyText="Aucun client trouvé"
              />
              <p className="text-xs text-gray-500">
                {clients.length} client(s) dans la base - <a href="/societes" className="text-blue-600 hover:underline" target="_blank">Gérer les clients</a>
              </p>
            </div>
          </div>
        );

      case 1: // Transport
        return (
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2">
              <Label>Véhicule (Optionnel - Auto-remplissage)</Label>
              <Combobox
                options={[
                  { value: "none", label: "Saisie manuelle" },
                  ...(vehicules || []).map((v) => ({
                    value: v.id,
                    label: `${v.matricule || 'N/A'} - ${v.immatriculation} - ${getTypeCamionLibelle(v.type_camion_id)} - ${v.capacite_tonnes}T`,
                    searchText: `${v.matricule || ''} ${v.immatriculation || ''} ${getTypeCamionLibelle(v.type_camion_id)} ${v.capacite_tonnes}`.toLowerCase()
                  }))
                ]}
                value={formData.vehicule_id || "none"}
                onValueChange={handleVehiculeChange}
                placeholder="Rechercher un véhicule"
                searchPlaceholder="Rechercher par matricule ou immatriculation..."
                emptyText="Aucun véhicule trouvé"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Chauffeur *
                {isFieldAutoFilled('chauffeur_id') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Auto</span>}
              </Label>
              <Combobox
                options={[
                  { value: "none", label: "Aucun" },
                  ...(chauffeurs || []).map((c) => {
                    // Trouver le nom de la société de transport si disponible
                    const societeNom = c.societe_transport_id 
                      ? (proprietaires || []).find(p => p.id === c.societe_transport_id)?.nom || ''
                      : '';
                    const proprietaireNom = c.proprietaire_id 
                      ? (proprietaires || []).find(p => p.id === c.proprietaire_id)?.nom || ''
                      : '';
                    
                    return {
                      value: c.id,
                      label: `${c.nom || 'N/A'} - ${c.telephone || 'N/A'}`,
                      // Recherche multi-critères: nom, téléphone, matricule, société
                      searchText: `${c.matricule || ''} ${c.nom || ''} ${c.telephone || ''} ${societeNom} ${proprietaireNom}`.toLowerCase()
                    };
                  })
                ]}
                value={formData.chauffeur_id || "none"}
                onValueChange={handleChauffeurChange}
                placeholder="Sélectionner un chauffeur"
                searchPlaceholder="Nom, téléphone, matricule ou société..."
                emptyText="Aucun chauffeur trouvé"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Type de camion *
                {isFieldAutoFilled('type_camion_id') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Auto</span>}
              </Label>
              <Select 
                key={`type-camion-${formData.type_camion_id || 'empty'}-${editMode}`} 
                value={formData.type_camion_id || 'none'} 
                onValueChange={(value) => setFormData({ ...formData, type_camion_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sélectionner</SelectItem>
                  {(typesCamion || []).map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.libelle || type.type_camion}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="capacite_t" className="flex items-center gap-2">
                Capacité max. du véhicule (T) *
                {isFieldAutoFilled('capacite_t') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Auto</span>}
              </Label>
              <div className="relative">
                <Input 
                  id="capacite_t"
                  type="number" 
                  step="0.5" 
                  min="0.5"
                  value={formData.capacite_t} 
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || !isNaN(parseFloat(value))) {
                      setFormData({ ...formData, capacite_t: value });
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === '' || parseFloat(value) < 0.5) {
                      setFormData({ ...formData, capacite_t: '10' });
                    }
                  }}
                  placeholder="Ex: 10, 20, 30"
                  required 
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                  tonnes
                </span>
              </div>
              <p className="text-xs text-gray-500">Capacité maximale que peut supporter le véhicule</p>
            </div>

            <div className="space-y-2">
              <Label>Nombre de camions</Label>
              <Input type="number" min="1" value={formData.nb_camions} onChange={(e) => setFormData({ ...formData, nb_camions: e.target.value })} required />
            </div>

            <div className="flex items-center space-x-2 pt-8">
              <Checkbox id="semi_remorque" checked={formData.semi_remorque} onCheckedChange={(checked) => setFormData({ ...formData, semi_remorque: checked })} />
              <Label htmlFor="semi_remorque" className="cursor-pointer">Semi-remorque</Label>
            </div>
          </div>
        );

      case 2: // Trajet & Tarification
        return (
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <LocationAutocomplete
                label="Ville de départ"
                placeholder="Rechercher un lieu de départ..."
                value={formData.ville_depart}
                onLocationSelect={(location) => {
                  setFormData(prev => ({ ...prev, ville_depart: location.label }));
                  setOriginLocation({
                    label: location.label,
                    latitude: location.latitude,
                    longitude: location.longitude
                  });
                }}
                country="CI"
              />
            </div>
            <div className="space-y-2">
              <LocationAutocomplete
                label="Ville de destination"
                placeholder="Rechercher un lieu de destination..."
                value={formData.ville_destination}
                onLocationSelect={(location) => {
                  setFormData(prev => ({ ...prev, ville_destination: location.label }));
                  setDestinationLocation({
                    label: location.label,
                    latitude: location.latitude,
                    longitude: location.longitude
                  });
                }}
                country="CI"
              />
            </div>
            
            {/* Étapes intermédiaires */}
            <div className="col-span-2 space-y-3 border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Étapes intermédiaires (optionnel)</Label>
                <span className="text-xs text-gray-500">{intermediateSteps.length}/30 étapes</span>
              </div>
              
              {intermediateSteps.map((step, index) => {
                // Si l'étape a déjà des coordonnées (opération existante), afficher en lecture seule
                const hasCoords = step.coords && step.coords.lat && step.coords.lng;
                
                return (
                  <div key={step.ordre || index} className="flex items-center gap-2 bg-white p-3 rounded border">
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      {hasCoords ? (
                        // Affichage en lecture seule pour les étapes existantes
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700">{step.ville}</span>
                          <span className="text-xs text-gray-500 bg-green-50 px-2 py-0.5 rounded">✓ Chargée</span>
                        </div>
                      ) : (
                        // Autocomplete pour les nouvelles étapes
                        <LocationAutocomplete
                          placeholder={`Étape ${index + 1}`}
                          value=""
                          onLocationSelect={(location) => {
                            const updatedSteps = [...intermediateSteps];
                            updatedSteps[index] = {
                              ordre: index + 1,
                              ville: location.label,
                              coords: {
                                lat: location.latitude,
                                lng: location.longitude
                              }
                            };
                            setIntermediateSteps(updatedSteps);
                            setFormData(prev => ({ ...prev, etapes: updatedSteps }));
                          }}
                          country="CI"
                        />
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updatedSteps = intermediateSteps.filter((_, i) => i !== index)
                          .map((s, i) => ({ ...s, ordre: i + 1 }));
                        setIntermediateSteps(updatedSteps);
                        setFormData(prev => ({ ...prev, etapes: updatedSteps }));
                      }}
                      className="flex-shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Supprimer cette étape"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (intermediateSteps.length < 30) {
                    const newStep = {
                      ordre: intermediateSteps.length + 1,
                      ville: '',
                      coords: null
                    };
                    setIntermediateSteps([...intermediateSteps, newStep]);
                  } else {
                    toast.error('Maximum 30 étapes intermédiaires');
                  }
                }}
                disabled={intermediateSteps.length >= 30 || !originLocation}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une étape
              </Button>
              
              {!originLocation && (
                <p className="text-xs text-amber-600 italic">
                  💡 Saisissez d'abord le point de départ pour ajouter des étapes
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Distance (km)</Label>
              <div className="flex gap-2 items-center">
                <Input 
                  type="number" 
                  min="0" 
                  step="1" 
                  value={formData.distance_km} 
                  onChange={(e) => setFormData({ ...formData, distance_km: e.target.value })} 
                  placeholder="450"
                  className="flex-1"
                  disabled={distanceLoading}
                />
                {distanceLoading && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Calcul en cours...</span>
                  </div>
                )}
                {calculatedDistance && !distanceLoading && (
                  <span className="text-xs text-emerald-600">
                    ✓ {calculatedDistance.from_cache ? 'Cache' : 'Calculé'}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Distance totale du trajet en kilomètres.
                {originLocation && destinationLocation && (
                  <span className="text-emerald-600"> Calcul automatique via Mapbox.</span>
                )}
              </p>
            </div>

            <div className="col-span-2 space-y-2">
              <div className="flex items-center space-x-2 p-3 border rounded bg-amber-50">
                <Checkbox 
                  id="is_multi_destination" 
                  checked={formData.is_multi_destination} 
                  onCheckedChange={(checked) => setFormData({ ...formData, is_multi_destination: checked })} 
                />
                <Label htmlFor="is_multi_destination" className="cursor-pointer font-normal">
                  🗺️ Trajet multi-destinations (plusieurs arrêts/destinations)
                </Label>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Transporteur</Label>
              <Input value={formData.transporteur} onChange={(e) => setFormData({ ...formData, transporteur: e.target.value })} placeholder="Nom société/transporteur" />
            </div>
            
            {/* Tarification : Transporteur uniquement */}
            <div className="space-y-2">
              <Label className="text-sm">Tarif Transporteur (FCFA)</Label>
              <Input 
                type="number" 
                min="0" 
                step="1000" 
                value={formData.tarif_transporteur} 
                onChange={(e) => setFormData({ ...formData, tarif_transporteur: e.target.value })} 
                placeholder="400000"
                className="font-medium"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Pays
                {isFieldAutoFilled('pays') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Auto</span>}
              </Label>
              <Input value={formData.pays} onChange={(e) => setFormData({ ...formData, pays: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Type transport</Label>
              <Select value={formData.intra_inter_land} onValueChange={(value) => setFormData({ ...formData, intra_inter_land: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Intraland">Intraland</SelectItem>
                  <SelectItem value="Interland">Interland</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 3: // Marchandise
        return (
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2">
              <Label>Descriptif de l'opération</Label>
              <Textarea 
                value={formData.descriptif_operation} 
                onChange={(e) => setFormData({ ...formData, descriptif_operation: e.target.value })} 
                rows={2}
                placeholder="Description détaillée de l'opération de transport"
              />
            </div>

            <div className="space-y-2">
              <Label>Type de marchandise*</Label>
              <Input 
                value={formData.type_marchandise} 
                onChange={(e) => setFormData({ ...formData, type_marchandise: e.target.value })} 
                placeholder="Ex: Céréales, Ciment, Matériel"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Poids de la marchandise (tonnes)</Label>
              <Input 
                type="number" 
                min="0" 
                step="0.1" 
                value={formData.poids_marchandise} 
                onChange={(e) => setFormData({ ...formData, poids_marchandise: e.target.value })} 
                placeholder="25.5"
              />
              <p className="text-xs text-gray-500">Poids réel du chargement transporté</p>
            </div>

            <div className="space-y-2">
              <Label>Volume (m³)</Label>
              <Input 
                type="number" 
                min="0" 
                step="0.1" 
                value={formData.volume_marchandise} 
                onChange={(e) => setFormData({ ...formData, volume_marchandise: e.target.value })} 
                placeholder="40"
              />
            </div>

            <div className="col-span-2 space-y-3 border p-3 rounded bg-gray-50">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="est_conteneur" 
                  checked={formData.est_conteneur} 
                  onCheckedChange={(checked) => setFormData({ ...formData, est_conteneur: checked })} 
                />
                <Label htmlFor="est_conteneur" className="cursor-pointer">
                  Transport de conteneur
                </Label>
              </div>

              {formData.est_conteneur && (
                <div className="space-y-2 ml-6">
                  <Label>Type de conteneur*</Label>
                  <Select 
                    value={formData.type_conteneur} 
                    onValueChange={(value) => setFormData({ ...formData, type_conteneur: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez le type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20_pieds">20 pieds (TEU)</SelectItem>
                      <SelectItem value="40_pieds">40 pieds (FEU)</SelectItem>
                      <SelectItem value="40_hc">40 pieds High Cube (HC)</SelectItem>
                      <SelectItem value="20_open_top">20 pieds Open Top</SelectItem>
                      <SelectItem value="40_open_top">40 pieds Open Top</SelectItem>
                      <SelectItem value="20_flat_rack">20 pieds Flat Rack</SelectItem>
                      <SelectItem value="40_flat_rack">40 pieds Flat Rack</SelectItem>
                      <SelectItem value="20_reefer">20 pieds Reefer (Frigo)</SelectItem>
                      <SelectItem value="40_reefer">40 pieds Reefer (Frigo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        );

      case 4: // Dates Lifecycle
        return (
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Date/heure de chargement prévu* <span className="text-red-500">Obligatoire</span></Label>
              <Input 
                type="datetime-local" 
                value={formData.datetime_chargement_prevu} 
                onChange={(e) => setFormData({ ...formData, datetime_chargement_prevu: e.target.value })} 
                required
              />
              <p className="text-xs text-gray-500">Horaire prévu pour le chargement de la marchandise</p>
            </div>

            <div className="space-y-2">
              <Label>Date/heure de chargement réel</Label>
              <Input 
                type="datetime-local" 
                value={formData.datetime_chargement_reel} 
                onChange={(e) => setFormData({ ...formData, datetime_chargement_reel: e.target.value })} 
              />
              <p className="text-xs text-gray-500">Horaire effectif du chargement (requis pour passer en cours)</p>
            </div>

            <div className="space-y-2">
              <Label>Date/heure déchargement prévu</Label>
              <Input 
                type="datetime-local" 
                value={formData.datetime_dechargement_prevu} 
                onChange={(e) => setFormData({ ...formData, datetime_dechargement_prevu: e.target.value })} 
              />
              <p className="text-xs text-gray-500">Horaire prévu pour le déchargement</p>
            </div>

            <div className="space-y-2">
              <Label>Date/heure déchargement réel</Label>
              <Input 
                type="datetime-local" 
                value={formData.datetime_dechargement_reel} 
                onChange={(e) => setFormData({ ...formData, datetime_dechargement_reel: e.target.value })} 
              />
              <p className="text-xs text-gray-500">Horaire effectif du déchargement (requis pour terminer)</p>
            </div>
          </div>
        );

      case 5: // Immobilisation & Documents
        return (
          <div className="space-y-6 py-4">
            {/* Section Immobilisation */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 border-t pt-4">
                <h3 className="text-lg font-semibold mb-3">Immobilisation</h3>
              </div>

              <div className="space-y-2">
                <Label>Jours d'immobilisation</Label>
                <Input 
                  type="number" 
                  min="0" 
                  value={formData.jours_immo} 
                  onChange={(e) => setFormData({ ...formData, jours_immo: e.target.value })} 
                  placeholder="0"
                />
                <p className="text-xs text-gray-500">Nombre de jours d'immobilisation du véhicule</p>
              </div>

              <div className="space-y-2">
                <Label>Tarif unitaire journalier (FCFA)</Label>
                <Input 
                  type="number" 
                  min="0" 
                  step="1000" 
                  value={formData.tarif_unitaire_immo} 
                  onChange={(e) => setFormData({ ...formData, tarif_unitaire_immo: e.target.value })} 
                  placeholder="50000"
                />
                <p className="text-xs text-gray-500">Coût journalier d'immobilisation</p>
              </div>

              {formData.jours_immo && formData.tarif_unitaire_immo && (
                <div className="col-span-2 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm font-semibold text-blue-900">
                    Coût total immobilisation: {(parseFloat(formData.jours_immo) * parseFloat(formData.tarif_unitaire_immo)).toLocaleString('fr-FR')} FCFA
                  </p>
                </div>
              )}

              <div className="col-span-2 space-y-2">
                <Label>Commentaire opérateur</Label>
                <Textarea value={formData.commentaire_operateur} onChange={(e) => setFormData({ ...formData, commentaire_operateur: e.target.value })} rows={3} />
              </div>
            </div>

            {/* Section Documents - Visible seulement en mode édition */}
            {editMode && currentOperationId && (
              <div className="space-y-4">
                <div className="col-span-2 border-t pt-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Documents
                  </h3>
                </div>

                {/* Stats des documents */}
                {documentsStats && (
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-blue-600">{documentsStats.total}</div>
                        <div className="text-sm text-gray-600">Total</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-green-600">{documentsStats.by_type?.bon_chargement || 0}</div>
                        <div className="text-sm text-gray-600">Bons chargement</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-purple-600">{documentsStats.by_type?.bon_dechargement || 0}</div>
                        <div className="text-sm text-gray-600">Bons déchargement</div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Boutons d'upload */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bon de chargement</Label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleDocumentUpload(e, 'bon_chargement')}
                      className="hidden"
                      id="upload-bon-chargement"
                      disabled={uploadingDocument}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => document.getElementById('upload-bon-chargement').click()}
                      disabled={uploadingDocument}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadingDocument ? 'Upload...' : 'Téléverser'}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Bon de déchargement</Label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleDocumentUpload(e, 'bon_dechargement')}
                      className="hidden"
                      id="upload-bon-dechargement"
                      disabled={uploadingDocument}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => document.getElementById('upload-bon-dechargement').click()}
                      disabled={uploadingDocument}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadingDocument ? 'Upload...' : 'Téléverser'}
                    </Button>
                  </div>
                </div>

                {/* Liste des documents */}
                <div className="space-y-2">
                  <Label>Documents ({documents.length})</Label>
                  {documents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Aucun document pour cette opération
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {documents.map((doc) => (
                        <Card key={doc.id}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {doc.content_type?.startsWith('image/') ? (
                                  <ImageIcon className="h-8 w-8 text-blue-600" />
                                ) : (
                                  <File className="h-8 w-8 text-red-600" />
                                )}
                                <div>
                                  <div className="font-medium">{doc.filename}</div>
                                  <div className="text-sm text-gray-500">
                                    {doc.type === 'bon_chargement' ? 'Bon de chargement' : 
                                     doc.type === 'bon_dechargement' ? 'Bon de déchargement' : 
                                     'Autre'} · {(doc.size_bytes / 1024).toFixed(1)} KB
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {new Date(doc.uploaded_at).toLocaleString('fr-FR')}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDocumentDownload(doc.id, doc.filename)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDocumentDelete(doc.id)}
                                >
                                  <X className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Message pour mode création */}
            {!editMode && (
              <div className="col-span-2 p-4 bg-amber-50 border border-amber-200 rounded">
                <p className="text-sm text-amber-800">
                  💡 Les documents pourront être ajoutés après la création de l'opération en mode modification.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="h-screen flex flex-col" data-testid="operations-page">
      {/* Header */}
      <div className="p-8 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Opérations</h1>
            <p className="text-gray-600 mt-2">Enregistrement des opérations de transport</p>
          </div>
          <div className="flex gap-2">
            <ProtectedButton 
              resource="operations" 
              action="export"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/operations?format=csv`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `operations_${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    toast.success('Export réussi');
                  } else {
                    toast.error('Erreur lors de l\'export');
                  }
                } catch (error) {
                  toast.error('Erreur lors de l\'export');
                }
              }} 
              variant="outline"
              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50" 
              data-testid="export-operations-btn"
            >
              <Download className="h-5 w-5 mr-2" />
              Exporter
            </ProtectedButton>
            <ProtectedButton 
              resource="operations" 
              action="create"
              onClick={() => { resetForm(); setEditMode(false); setDialogOpen(true); }} 
              className="bg-emerald-600 hover:bg-emerald-700" 
              data-testid="add-operation-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              Nouvelle opération
            </ProtectedButton>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col px-8">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-4">
          <TabsTrigger value="liste">
            <FileText className="w-4 h-4 mr-2" />
            Liste
          </TabsTrigger>
          <TabsTrigger value="carte">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Carte temps réel
          </TabsTrigger>
        </TabsList>

        {/* Contenu onglet Liste */}
        <TabsContent value="liste" className="flex-1 overflow-y-auto space-y-6 m-0">
          <div className="space-y-6 pb-8">

      {/* Filters Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Filtres</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="gap-2"
            >
              <Search className="h-4 w-4" />
              {showAdvancedFilters ? 'Masquer les filtres' : 'Recherche avancée'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label htmlFor="filter-search">Recherche</Label>
              <Input
                id="filter-search"
                placeholder="Matricule, BL, trajet..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label htmlFor="filter-statut">Statut</Label>
              <Select value={filters.statut} onValueChange={(value) => setFilters({...filters, statut: value})}>
                <SelectTrigger id="filter-statut">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="Nouveau">Nouveau</SelectItem>
                  <SelectItem value="En attente">En attente</SelectItem>
                  <SelectItem value="En cours">En cours</SelectItem>
                  <SelectItem value="En transit">En transit</SelectItem>
                  <SelectItem value="En chargement">En chargement</SelectItem>
                  <SelectItem value="En déchargement">En déchargement</SelectItem>
                  <SelectItem value="Chargé">Chargé</SelectItem>
                  <SelectItem value="Validé">Validé</SelectItem>
                  <SelectItem value="Terminée">Terminée</SelectItem>
                  <SelectItem value="Facturé">Facturé</SelectItem>
                  <SelectItem value="Reporté">Reporté</SelectItem>
                  <SelectItem value="Annulée">Annulée</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Chauffeur Filter */}
            <div className="space-y-2">
              <Label htmlFor="filter-chauffeur">Chauffeur</Label>
              <Select value={filters.chauffeur} onValueChange={(value) => setFilters({...filters, chauffeur: value})}>
                <SelectTrigger id="filter-chauffeur">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {(chauffeurs || []).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Transporteur Filter */}
            <div className="space-y-2">
              <Label htmlFor="filter-transporteur">Transporteur</Label>
              <Select value={filters.transporteur} onValueChange={(value) => setFilters({...filters, transporteur: value})}>
                <SelectTrigger id="filter-transporteur">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {uniqueTransporteurs.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div className="space-y-2">
              <Label htmlFor="filter-date-from">Date début</Label>
              <Input
                id="filter-date-from"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              />
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <Label htmlFor="filter-date-to">Date fin</Label>
              <Input
                id="filter-date-to"
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              />
            </div>

            {/* Client Filter */}
            <div className="space-y-2">
              <Label htmlFor="filter-client">Client / Société</Label>
              <Select value={filters.client} onValueChange={(value) => setFilters({...filters, client: value})}>
                <SelectTrigger id="filter-client">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {(clients || []).map(c => (
                    <SelectItem key={c.id} value={c.nom}>{c.matricule} - {c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Immatriculation Filter */}
            <div className="space-y-2">
              <Label htmlFor="filter-immat">Plaque d'immatriculation</Label>
              <Input
                id="filter-immat"
                placeholder="Ex: CI-ABJ-1234-AA"
                value={filters.immatriculation}
                onChange={(e) => setFilters({...filters, immatriculation: e.target.value})}
              />
            </div>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <>
              {/* Advanced Filters - Second Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t">
            {/* Date Chargement From */}
            <div className="space-y-2">
              <Label htmlFor="filter-chargement-from">Date chargement (début)</Label>
              <Input
                id="filter-chargement-from"
                type="date"
                value={filters.dateChargementFrom}
                onChange={(e) => setFilters({...filters, dateChargementFrom: e.target.value})}
              />
            </div>

            {/* Date Chargement To */}
            <div className="space-y-2">
              <Label htmlFor="filter-chargement-to">Date chargement (fin)</Label>
              <Input
                id="filter-chargement-to"
                type="date"
                value={filters.dateChargementTo}
                onChange={(e) => setFilters({...filters, dateChargementTo: e.target.value})}
              />
            </div>

            {/* Date Dechargement From */}
            <div className="space-y-2">
              <Label htmlFor="filter-dechargement-from">Date déchargement (début)</Label>
              <Input
                id="filter-dechargement-from"
                type="date"
                value={filters.dateDechargementFrom}
                onChange={(e) => setFilters({...filters, dateDechargementFrom: e.target.value})}
              />
            </div>

            {/* Date Dechargement To */}
            <div className="space-y-2">
              <Label htmlFor="filter-dechargement-to">Date déchargement (fin)</Label>
              <Input
                id="filter-dechargement-to"
                type="date"
                value={filters.dateDechargementTo}
                onChange={(e) => setFilters({...filters, dateDechargementTo: e.target.value})}
              />
            </div>
          </div>

          {/* Advanced Filters - Third Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t">
            {/* Type de véhicule */}
            <div className="space-y-2">
              <Label htmlFor="filter-type-vehicule">Type de véhicule</Label>
              <Select 
                value={filters.type_vehicule} 
                onValueChange={(value) => setFilters({...filters, type_vehicule: value})}
              >
                <SelectTrigger id="filter-type-vehicule">
                  <SelectValue placeholder="Tous les types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {(typesCamion || []).map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.type_camion}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type de marchandise */}
            <div className="space-y-2">
              <Label htmlFor="filter-type-marchandise">Type de marchandise</Label>
              <Input
                id="filter-type-marchandise"
                placeholder="Ex: Ciment, Céréales..."
                value={filters.type_marchandise}
                onChange={(e) => setFilters({...filters, type_marchandise: e.target.value})}
              />
            </div>

            {/* Ville de départ */}
            <div className="space-y-2">
              <Label htmlFor="filter-ville-depart">Ville de départ</Label>
              <Input
                id="filter-ville-depart"
                placeholder="Ex: Abidjan, Bouaké..."
                value={filters.ville_depart}
                onChange={(e) => setFilters({...filters, ville_depart: e.target.value})}
              />
            </div>
          </div>

          {/* Advanced Filters - Fourth Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t">
            {/* Ville de destination */}
            <div className="space-y-2">
              <Label htmlFor="filter-ville-destination">Ville de destination</Label>
              <Input
                id="filter-ville-destination"
                placeholder="Ex: Yamoussoukro, San Pedro..."
                value={filters.ville_destination}
                onChange={(e) => setFilters({...filters, ville_destination: e.target.value})}
              />
            </div>

            {/* Multi-destination Filter */}
            <div className="space-y-2">
              <Label htmlFor="filter-multi-destination">Type de trajet</Label>
              <Select 
                value={filters.is_multi_destination} 
                onValueChange={(value) => setFilters({...filters, is_multi_destination: value})}
              >
                <SelectTrigger id="filter-multi-destination">
                  <SelectValue placeholder="Tous les trajets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les trajets</SelectItem>
                  <SelectItem value="false">🚚 Trajet simple</SelectItem>
                  <SelectItem value="true">🗺️ Multi-destinations</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reset Filters Button */}
            <div className="space-y-2 flex items-end">
              <Button
                variant="outline"
                onClick={() => setFilters({
                  search: '',
                  statut: 'all',
                  dateFrom: '',
                  dateTo: '',
                  chauffeur: 'all',
                  transporteur: 'all',
                  client: 'all',
                  immatriculation: '',
                  dateChargementFrom: '',
                  dateChargementTo: '',
                  dateDechargementFrom: '',
                  dateDechargementTo: '',
                  type_vehicule: 'all',
                  type_marchandise: '',
                  ville_depart: '',
                  ville_destination: '',
                  is_multi_destination: 'all'
                })}
                className="w-full"
              >
                Réinitialiser
              </Button>
            </div>

            {/* Active filters count */}
            <div className="space-y-2 flex items-end">
              <div className="text-sm text-gray-600">
                {filteredOperations.length} / {operations.length} opérations
              </div>
            </div>
          </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" />
            Historique des opérations ({operations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions Toolbar */}
          {selectedIds.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="font-medium text-blue-900">
                  {selectedIds.length} opération(s) sélectionnée(s)
                </span>
              </div>

              <div className="flex items-center gap-2">
                {/* Bulk Assign */}
                <Select onValueChange={(value) => handleBulkAssign(value)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Assigner à..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assigné</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>{user.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Bulk Status Change */}
                <Select onValueChange={(value) => handleBulkStatusChange(value)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Changer statut..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nouveau">Nouveau</SelectItem>
                    <SelectItem value="En attente">En attente</SelectItem>
                    <SelectItem value="En cours">En cours</SelectItem>
                    <SelectItem value="En transit">En transit</SelectItem>
                    <SelectItem value="En chargement">En chargement</SelectItem>
                    <SelectItem value="En déchargement">En déchargement</SelectItem>
                    <SelectItem value="Chargé">Chargé</SelectItem>
                    <SelectItem value="Validé">Validé</SelectItem>
                    <SelectItem value="Terminée">Terminée</SelectItem>
                    <SelectItem value="Reporté">Reporté</SelectItem>
                    <SelectItem value="Annulée">Annulée</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedIds([])}
                  className="gap-2"
                >
                  Annuler
                </Button>

                {hasPermission('operations', 'delete') && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Supprimer
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700 w-12">
                    <CustomCheckbox
                      checked={selectedIds.length === paginatedOperations.length && paginatedOperations.length > 0}
                      onChange={(checked) => checked ? handleSelectAll() : handleDeselectAll()}
                    />
                  </th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Matricule</th>
                  <SortableHeader sortKey="date_operation" label="Date création" />
                  <SortableHeader sortKey="ville_depart" label="Ville de départ" />
                  <SortableHeader sortKey="ville_destination" label="Ville de destination" />
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Marchandise</th>
                  <SortableHeader sortKey="transporteur" label="Transporteur" />
                  <SortableHeader sortKey="chauffeur" label="Chauffeur" />
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Immatriculation</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Type véhicule</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Poids (Tonne)</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Type conteneur</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Distance (km)</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Tarif/KM</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Coût/T</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Client</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Chargement prévu</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Chargement réel</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Déchargement prévu</th>
                  <SortableHeader sortKey="tarif_transport" label="Tarif Transp." />
                  {canViewClientPricing && (
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Tarif Client</th>
                  )}
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Jours Immo</th>
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Total Immo</th>
                  <SortableHeader sortKey="statut" label="Statut" />
                  <th className="text-left py-3 px-3 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOperations.map((op) => {
                  // Calcul du total immobilisation (nouveaux champs lifecycle ont priorité)
                  const joursImmo = op.jours_immo || op.jours_immobilisation || 0;
                  const coutUnitImmo = op.tarif_unitaire_immo || op.tarif_immobilisation_journalier || 0;
                  const totalImmo = joursImmo * coutUnitImmo;
                  
                  return (
                  <tr key={op.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <CustomCheckbox
                        checked={selectedIds.includes(op.id)}
                        onChange={() => handleToggleSelect(op.id)}
                      />
                    </td>
                    <td className="py-3 px-3 font-mono text-xs text-gray-600">{op.matricule || '-'}</td>
                    <td className="py-3 px-3 text-gray-700">{new Date(op.date_operation).toLocaleDateString('fr-FR')}</td>
                    <td className="py-3 px-3 text-gray-700 text-sm">
                      {op.ville_depart || '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700 text-sm">
                      {(() => {
                        // Si l'opération a des étapes, afficher le trajet complet
                        if (op.etapes && Array.isArray(op.etapes) && op.etapes.length > 0) {
                          const etapesNoms = op.etapes
                            .sort((a, b) => a.ordre - b.ordre)
                            .map(e => e.ville.split(',')[0]) // Prendre seulement le nom de ville (avant la première virgule)
                            .join(' → ');
                          
                          return (
                            <div className="flex items-center gap-1">
                              <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium" title={`${op.etapes.length} étape(s)`}>
                                🔸 {op.etapes.length}
                              </span>
                              <span className="text-xs text-gray-600" title={`${op.ville_depart?.split(',')[0]} → ${etapesNoms} → ${op.ville_destination?.split(',')[0]}`}>
                                {etapesNoms} → {op.ville_destination?.split(',')[0] || '-'}
                              </span>
                            </div>
                          );
                        }
                        // Sinon, afficher simplement la destination
                        return op.ville_destination || '-';
                      })()}
                    </td>
                    <td className="py-3 px-3 text-gray-700">
                      <div className="max-w-[120px]">
                        {op.type_marchandise ? (
                          <span className="text-sm">{op.type_marchandise}</span>
                        ) : '-'}
                        {op.est_conteneur && (
                          <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">📦</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 font-medium text-gray-900">{op.transporteur || '-'}</td>
                    <td className="py-3 px-3 text-gray-700">{op.chauffeur_id ? getChauffeurNom(op.chauffeur_id) : '-'}</td>
                    <td className="py-3 px-3 text-gray-700 text-xs">
                      {op.vehicle_snapshot?.immatriculation || '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700 text-sm">
                      {op.type_camion_id ? getTypeCamionLibelle(op.type_camion_id) : '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700 text-sm font-medium">
                      {op.poids_marchandise ? `${op.poids_marchandise} T` : '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700 text-sm">
                      {op.type_conteneur || 'N/A'}
                    </td>
                    <td className="py-3 px-3 text-gray-700 text-sm">
                      <div className="flex items-center gap-1">
                        <span>{op.distance_km ? `${op.distance_km} km` : '-'}</span>
                        {op.is_multi_destination && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium" title="Trajet multi-destinations">
                            🗺️
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-gray-700 text-sm font-medium">
                      {op.tarif_transport && op.distance_km && op.distance_km > 0 
                        ? `${(op.tarif_transport / op.distance_km).toLocaleString('fr-FR', {minimumFractionDigits: 0, maximumFractionDigits: 0})} FCFA`
                        : '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700 text-sm font-medium">
                      {op.tarif_transport && op.poids_marchandise && op.poids_marchandise > 0 
                        ? `${(op.tarif_transport / op.poids_marchandise).toLocaleString('fr-FR', {minimumFractionDigits: 0, maximumFractionDigits: 0})} FCFA`
                        : '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700">{op.client_nom || '-'}</td>
                    <td className="py-3 px-3 text-gray-700 text-xs">
                      {op.datetime_chargement_prevu ? (
                        new Date(op.datetime_chargement_prevu).toLocaleString('fr-FR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })
                      ) : '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700 text-xs">
                      {op.datetime_chargement_reel ? (
                        <span className="text-green-700 font-medium">
                          {new Date(op.datetime_chargement_reel).toLocaleString('fr-FR', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700 text-xs">
                      {op.datetime_dechargement_prevu ? (
                        new Date(op.datetime_dechargement_prevu).toLocaleString('fr-FR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })
                      ) : '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700">
                      {op.tarif_transporteur ? `${op.tarif_transporteur.toLocaleString()} FCFA` : (op.tarif_transport ? `${op.tarif_transport.toLocaleString()} FCFA` : '-')}
                    </td>
                    {canViewClientPricing && (
                      <td className="py-3 px-3 text-emerald-700 font-semibold">
                        {op.tarif_client ? `${op.tarif_client.toLocaleString()} FCFA` : '-'}
                      </td>
                    )}
                    <td className="py-3 px-3 text-gray-700 text-center">
                      {op.jours_immo || op.jours_immobilisation || '-'}
                    </td>
                    <td className="py-3 px-3 text-gray-700 font-semibold">
                      {totalImmo > 0 ? `${totalImmo.toLocaleString()} FCFA` : '-'}
                    </td>
                    <td className="py-3 px-3">
                      <Select value={op.statut || 'En cours'} onValueChange={(value) => handleStatusChange(op.id, value)}>
                        <SelectTrigger className={`w-32 h-7 text-xs ${getStatutBadgeColor(op.statut)}`} data-testid={`operation-status-select-${op.matricule}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Nouveau">Nouveau</SelectItem>
                          <SelectItem value="En attente">En attente</SelectItem>
                          <SelectItem value="En cours">En cours</SelectItem>
                          <SelectItem value="En transit">En transit</SelectItem>
                          <SelectItem value="En chargement">En chargement</SelectItem>
                          <SelectItem value="En déchargement">En déchargement</SelectItem>
                          <SelectItem value="Chargé">Chargé</SelectItem>
                          <SelectItem value="Validé">Validé</SelectItem>
                          <SelectItem value="Terminée" data-testid="status-terminee">Terminée</SelectItem>
                          <SelectItem value="Facturé">Facturé</SelectItem>
                          <SelectItem value="Reporté">Reporté</SelectItem>
                          <SelectItem value="Annulée">Annulée</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedOperation(op);
                            setDetailsDialogOpen(true);
                            fetchDocuments(op.id);
                          }}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditOperation(op)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDuplicateOperation(op.id)}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          title="Dupliquer"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {hasPermission('operations', 'delete') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSingleOperation(op.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredOperations.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                {operations.length === 0 ? 
                  'Aucune opération enregistrée' : 
                  'Aucune opération ne correspond aux filtres'
                }
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredOperations.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredOperations.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer {selectedIds.length} opération(s) ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="operation-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Modifier l\'opération' : 'Nouvelle opération de transport'}</DialogTitle>
            <DialogDescription>
              {editMode ? 'Modifiez les informations de l\'opération' : 'Créez une nouvelle opération en suivant les étapes'}
            </DialogDescription>
          </DialogHeader>
          
          {false ? (
            // DÉSACTIVÉ: Mode édition avec Tabs
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">
                  <FileText className="h-4 w-4 mr-2" />
                  Détails
                </TabsTrigger>
                <TabsTrigger value="documents">
                  <Upload className="h-4 w-4 mr-2" />
                  Documents {documentsStats && `(${documentsStats.total})`}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details">
                <form id="operation-form" onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Date*</Label>
                <Input type="date" value={formData.date_operation} onChange={(e) => setFormData({ ...formData, date_operation: e.target.value })} required data-testid="op-date-input" />
              </div>
              
              {/* Assignation d'opérateur (champ principal) */}
              <div className="space-y-2">
                <LabelWithTooltip 
                  label="Opérateur assigné*" 
                  tooltip="Utilisateur responsable de la gestion et du suivi de cette opération de transport. Il peut modifier le statut et ajouter des commentaires."
                />
                <Select 
                  value={formData.operateur_assigne || 'none'} 
                  onValueChange={(value) => {
                    const userName = value === 'none' ? '' : (users || []).find(u => u.id === value)?.nom || '';
                    setFormData({ 
                      ...formData, 
                      operateur_assigne: value === 'none' ? '' : value,
                      operateur: userName // Auto-remplir avec le nom pour compatibilité backend
                    });
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un utilisateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assigné</SelectItem>
                    {(users || []).map((user) => (
                      <SelectItem key={user.id} value={user.id}>{user.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Client avec autocomplétion */}
              <div className="space-y-2">
                <Label>Client (Obligatoire)</Label>
                <Combobox
                  options={[
                    { value: "none", label: "Sélectionner un client" },
                    ...(clients || []).map((cl) => ({
                      value: cl.id,
                      label: `${cl.matricule} - ${cl.nom}`,
                      searchText: `${cl.matricule} ${cl.nom} ${cl.nom_contact || ''} ${cl.ville || ''}`.toLowerCase()
                    }))
                  ]}
                  value={formData.client_id || "none"}
                  onValueChange={handleClientChange}
                  placeholder="Rechercher un client"
                  searchPlaceholder="Matricule, nom, contact..."
                  emptyText="Aucun client trouvé"
                />
                <p className="text-xs text-gray-500">
                  {clients.length} client(s) dans la base - <a href="/societes" className="text-blue-600 hover:underline" target="_blank">Gérer les clients</a>
                </p>
              </div>
              
              {/* Statut de l'opération */}
              <div className="space-y-2">
                <Label>Statut*</Label>
                <Select 
                  value={formData.statut} 
                  onValueChange={(value) => {
                    // Si on passe à "Terminée", vérifier que les infos obligatoires sont renseignées
                    if (value === 'Terminée') {
                      const missingFields = [];
                      
                      // Vérifier date/heure déchargement réel
                      // Check datetime_chargement_reel
                      if (!formData.datetime_chargement_reel || formData.datetime_chargement_reel === '') {
                        missingFields.push('datetime_chargement_reel');
                      }
                      
                      // Check datetime_dechargement_reel
                      if (!formData.datetime_dechargement_reel || formData.datetime_dechargement_reel === '') {
                        missingFields.push('datetime_dechargement_reel');
                      }
                      
                      // Check bon_chargement_id
                      if (!formData.bon_chargement_id || formData.bon_chargement_id === '') {
                        missingFields.push('bon_chargement_id');
                      }
                      
                      // Vérifier jours immobilisation (peut être 0, mais doit être défini)
                      if (formData.jours_immo === '' || formData.jours_immo === null || formData.jours_immo === undefined) {
                        missingFields.push('jours_immobilisation');
                      }
                      
                      if (missingFields.length > 0) {
                        console.log('Champs manquants:', missingFields); // Debug
                        setOperationToClose({ ...formData, missingFields });
                        setValidationDialogOpen(true);
                        return; // Ne pas changer le statut
                      }
                    }
                    setFormData({ ...formData, statut: value });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nouveau">Nouveau</SelectItem>
                    <SelectItem value="En attente">En attente</SelectItem>
                    <SelectItem value="En cours">En cours</SelectItem>
                    <SelectItem value="En transit">En transit</SelectItem>
                    <SelectItem value="En chargement">En chargement</SelectItem>
                    <SelectItem value="En déchargement">En déchargement</SelectItem>
                    <SelectItem value="Chargé">Chargé</SelectItem>
                    <SelectItem value="Validé">Validé</SelectItem>
                    <SelectItem value="Terminée">Terminée</SelectItem>
                    <SelectItem value="Reporté">Reporté</SelectItem>
                    <SelectItem value="Annulée">Annulée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Sélection Véhicule - Auto-fill multiple champs */}
              <div className="col-span-2 space-y-2">
                <Label className="flex items-center gap-2">
                  🚛 Véhicule (auto-remplit les données)
                  <span className="text-xs text-gray-500">Optionnel - Recherchez par matricule ou immatriculation</span>
                </Label>
                <Combobox
                  options={[
                    { value: "none", label: "Saisie manuelle" },
                    ...(vehicules || []).map((v) => ({
                      value: v.id,
                      label: `${v.matricule || 'N/A'} - ${v.immatriculation} - ${getTypeCamionLibelle(v.type_camion_id)} - ${v.capacite_tonnes}T`,
                      searchText: `${v.matricule || ''} ${v.immatriculation || ''} ${getTypeCamionLibelle(v.type_camion_id)} ${v.capacite_tonnes}`.toLowerCase()
                    }))
                  ]}
                  value={formData.vehicule_id || "none"}
                  onValueChange={handleVehiculeChange}
                  placeholder="Rechercher un véhicule"
                  searchPlaceholder="Matricule, immatriculation ou type..."
                  emptyText="Aucun véhicule trouvé"
                />
              </div>
              <div className="space-y-2">
                <LabelWithTooltip 
                  label="Chauffeur" 
                  tooltip="Chauffeur qui conduira le véhicule pour cette opération. Optionnel si le véhicule n'a pas de chauffeur assigné ou si vous ne le connaissez pas encore."
                >
                  {isFieldAutoFilled('chauffeur_id') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded ml-2">✓ Auto</span>}
                </LabelWithTooltip>
                <Combobox
                  options={[
                    { value: "none", label: "Aucun" },
                    ...(chauffeurs || []).map((c) => {
                      // Trouver le nom de la société de transport si disponible
                      const societeNom = c.societe_transport_id 
                        ? (proprietaires || []).find(p => p.id === c.societe_transport_id)?.nom || ''
                        : '';
                      const proprietaireNom = c.proprietaire_id 
                        ? (proprietaires || []).find(p => p.id === c.proprietaire_id)?.nom || ''
                        : '';
                      
                      return {
                        value: c.id,
                        label: `${c.nom || 'N/A'} - ${c.telephone || 'N/A'}`,
                        // Recherche multi-critères: nom, téléphone, matricule, société
                        searchText: `${c.matricule || ''} ${c.nom || ''} ${c.telephone || ''} ${societeNom} ${proprietaireNom}`.toLowerCase()
                      };
                    })
                  ]}
                  value={formData.chauffeur_id || "none"}
                  onValueChange={handleChauffeurChange}
                  placeholder="Rechercher un chauffeur"
                  searchPlaceholder="Nom, téléphone, matricule ou société..."
                  emptyText="Aucun chauffeur trouvé"
                />
              </div>
              <div className="space-y-2">
                <LabelWithTooltip 
                  label="Propriétaire (Partenaire)" 
                  tooltip="Société de transport ou partenaire qui possède le véhicule utilisé pour cette opération. Optionnel."
                >
                  {isFieldAutoFilled('proprietaire_id') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded ml-2">✓ Auto</span>}
                </LabelWithTooltip>
                <Combobox
                  options={[
                    { value: "none", label: "Aucun" },
                    ...(proprietaires || []).map((p) => ({
                      value: p.id,
                      label: p.nom,
                      searchText: (p.nom || '').toLowerCase()
                    }))
                  ]}
                  value={formData.proprietaire_id || "none"}
                  onValueChange={handleProprietaireChange}
                  placeholder="Rechercher un propriétaire"
                  searchPlaceholder="Nom..."
                  emptyText="Aucun propriétaire trouvé"
                />
              </div>
              <div className="space-y-2">
                <Label>Client (Obligatoire)</Label>
                <Combobox
                  options={[
                    { value: "none", label: "Sélectionner un client" },
                    ...(clients || []).map((cl) => ({
                      value: cl.id,
                      label: `${cl.matricule} - ${cl.nom}`,
                      searchText: `${cl.matricule} ${cl.nom} ${cl.nom_contact || ''} ${cl.ville || ''}`.toLowerCase()
                    }))
                  ]}
                  value={formData.client_id || "none"}
                  onValueChange={handleClientChange}
                  placeholder="Rechercher un client"
                  searchPlaceholder="Matricule, nom, contact..."
                  emptyText="Aucun client trouvé"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Numéro de contact
                  {isFieldAutoFilled('numero_contact') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Auto</span>}
                </Label>
                <Input value={formData.numero_contact} onChange={(e) => setFormData({ ...formData, numero_contact: e.target.value })} required data-testid="op-contact-input" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Type de camion
                  {isFieldAutoFilled('type_camion_id') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Auto</span>}
                </Label>
                <Select value={formData.type_camion_id} onValueChange={(value) => setFormData({ ...formData, type_camion_id: value })}>
                  <SelectTrigger data-testid="op-type-select"><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
                  <SelectContent>
                    {(typesCamion || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.libelle}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nombre de camions</Label>
                <Input type="number" min="1" value={formData.nb_camions} onChange={(e) => setFormData({ ...formData, nb_camions: e.target.value })} required data-testid="op-nb-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="capacite_t" className="flex items-center gap-2">
                  Capacité max. du véhicule (T) *
                  {isFieldAutoFilled('capacite_t') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Auto</span>}
                </Label>
                <div className="relative">
                  <Input 
                    id="capacite_t"
                    type="number" 
                    step="0.5" 
                    min="0.5"
                    value={formData.capacite_t} 
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty for user to type, or valid numbers
                      if (value === '' || !isNaN(parseFloat(value))) {
                        setFormData({ ...formData, capacite_t: value });
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure we have a valid number on blur
                      const value = e.target.value;
                      if (value === '' || parseFloat(value) < 0.5) {
                        setFormData({ ...formData, capacite_t: '10' }); // Default to 10
                      }
                    }}
                    placeholder="Ex: 10, 20, 30"
                    required 
                    data-testid="op-capacite-input"
                    className="pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                    tonnes
                  </span>
                </div>
                <p className="text-xs text-gray-500">Capacité maximale que peut supporter le véhicule</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Tracteur (optionnel)
                  {isFieldAutoFilled('tracteur') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Auto</span>}
                </Label>
                <Input value={formData.tracteur} onChange={(e) => setFormData({ ...formData, tracteur: e.target.value })} data-testid="op-tracteur-input" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Pays
                  {isFieldAutoFilled('pays') && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Auto</span>}
                </Label>
                <Input value={formData.pays} onChange={(e) => setFormData({ ...formData, pays: e.target.value })} required data-testid="op-pays-input" />
              </div>
              <div className="space-y-2">
                <Label>Type transport</Label>
                <Select value={formData.intra_inter_land} onValueChange={(value) => setFormData({ ...formData, intra_inter_land: value })}>
                  <SelectTrigger data-testid="op-transport-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Intraland">Intraland</SelectItem>
                    <SelectItem value="Interland">Interland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <Checkbox id="semi_remorque" checked={formData.semi_remorque} onCheckedChange={(checked) => setFormData({ ...formData, semi_remorque: checked })} data-testid="op-semi-checkbox" />
                <Label htmlFor="semi_remorque" className="cursor-pointer">Semi-remorque</Label>
              </div>

              {/* Section Trajet */}
              <div className="col-span-2 border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3">Informations Trajet & Tarif</h3>
              </div>
              <div className="space-y-2">
                <LocationAutocomplete
                  label="Ville de départ"
                  placeholder="Rechercher un lieu de départ..."
                  value={formData.ville_depart}
                  onLocationSelect={(location) => {
                    setFormData(prev => ({ ...prev, ville_depart: location.label }));
                    setOriginLocation({
                      label: location.label,
                      latitude: location.latitude,
                      longitude: location.longitude
                    });
                  }}
                  country="CI"
                />
              </div>
              <div className="space-y-2">
                <LocationAutocomplete
                  label="Ville de destination"
                  placeholder="Rechercher un lieu de destination..."
                  value={formData.ville_destination}
                  onLocationSelect={(location) => {
                    setFormData(prev => ({ ...prev, ville_destination: location.label }));
                    setDestinationLocation({
                      label: location.label,
                      latitude: location.latitude,
                      longitude: location.longitude
                    });
                  }}
                  country="CI"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Distance (km)</Label>
                <div className="flex gap-2 items-center">
                  <Input 
                    type="number" 
                    min="0" 
                    step="1" 
                    value={formData.distance_km} 
                    onChange={(e) => setFormData({ ...formData, distance_km: e.target.value })} 
                    placeholder="450"
                    className="flex-1"
                    disabled={distanceLoading}
                  />
                  {distanceLoading && (
                    <div className="flex items-center gap-2 text-sm text-emerald-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Calcul en cours...</span>
                    </div>
                  )}
                  {calculatedDistance && !distanceLoading && (
                    <span className="text-xs text-emerald-600">
                      ✓ {calculatedDistance.from_cache ? 'Cache' : 'Calculé'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Distance totale du trajet en kilomètres.
                  {originLocation && destinationLocation && (
                    <span className="text-emerald-600"> Calcul automatique via Mapbox.</span>
                  )}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Transporteur</Label>
                <Input value={formData.transporteur} onChange={(e) => setFormData({ ...formData, transporteur: e.target.value })} placeholder="Nom société/transporteur" />
              </div>
              <div className="space-y-2">
                <Label>Tarif transport (FCFA)</Label>
                <Input type="number" min="0" step="1000" value={formData.tarif_transport} onChange={(e) => setFormData({ ...formData, tarif_transport: e.target.value })} placeholder="500000" />
              </div>

              {/* === SECTION MARCHANDISE ET CHARGEMENT === */}
              <div className="col-span-2 border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3">Dates de l'opération</h3>
              </div>

              <div className="space-y-2">
                <Label>Date/heure de chargement prévu* <span className="text-red-500">Obligatoire</span></Label>
                <Input 
                  type="datetime-local" 
                  value={formData.datetime_chargement_prevu} 
                  onChange={(e) => setFormData({ ...formData, datetime_chargement_prevu: e.target.value })} 
                  required
                />
                <p className="text-xs text-gray-500">Horaire prévu pour le chargement de la marchandise</p>
              </div>

              <div className="space-y-2">
                <Label>Date/heure de chargement réel</Label>
                <Input 
                  type="datetime-local" 
                  value={formData.datetime_chargement_reel} 
                  onChange={(e) => setFormData({ ...formData, datetime_chargement_reel: e.target.value })} 
                />
                <p className="text-xs text-gray-500">Horaire effectif du chargement (requis pour passer en cours)</p>
              </div>

              <div className="space-y-2">
                <Label>Date/heure déchargement prévu</Label>
                <Input 
                  type="datetime-local" 
                  value={formData.datetime_dechargement_prevu} 
                  onChange={(e) => setFormData({ ...formData, datetime_dechargement_prevu: e.target.value })} 
                />
                <p className="text-xs text-gray-500">Horaire prévu pour le déchargement</p>
              </div>

              <div className="space-y-2">
                <Label>Date/heure déchargement réel</Label>
                <Input 
                  type="datetime-local" 
                  value={formData.datetime_dechargement_reel} 
                  onChange={(e) => setFormData({ ...formData, datetime_dechargement_reel: e.target.value })} 
                />
                <p className="text-xs text-gray-500">Horaire effectif du déchargement (requis pour terminer)</p>
              </div>

              {/* === SECTION MARCHANDISE === */}
              <div className="col-span-2 border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3">Détails marchandise</h3>
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Descriptif de l'opération</Label>
                <Textarea 
                  value={formData.descriptif_operation} 
                  onChange={(e) => setFormData({ ...formData, descriptif_operation: e.target.value })} 
                  rows={2}
                  placeholder="Description détaillée de l'opération de transport"
                />
              </div>

              <div className="space-y-2">
                <Label>Type de marchandise*</Label>
                <Input 
                  value={formData.type_marchandise} 
                  onChange={(e) => setFormData({ ...formData, type_marchandise: e.target.value })} 
                  placeholder="Ex: Céréales, Ciment, Matériel"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Poids de la marchandise (tonnes)</Label>
                <Input 
                  type="number" 
                  min="0" 
                  step="0.1" 
                  value={formData.poids_marchandise} 
                  onChange={(e) => setFormData({ ...formData, poids_marchandise: e.target.value })} 
                  placeholder="25.5"
                />
                <p className="text-xs text-gray-500">Poids réel du chargement transporté</p>
              </div>

              <div className="space-y-2">
                <Label>Volume (m³)</Label>
                <Input 
                  type="number" 
                  min="0" 
                  step="0.1" 
                  value={formData.volume_marchandise} 
                  onChange={(e) => setFormData({ ...formData, volume_marchandise: e.target.value })} 
                  placeholder="40"
                />
              </div>

              <div className="col-span-2 space-y-3 border p-3 rounded bg-gray-50">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="est_conteneur" 
                    checked={formData.est_conteneur} 
                    onCheckedChange={(checked) => setFormData({ ...formData, est_conteneur: checked })} 
                  />
                  <Label htmlFor="est_conteneur" className="cursor-pointer">
                    Transport de conteneur
                  </Label>
                </div>

                {formData.est_conteneur && (
                  <div className="space-y-2 ml-6">
                    <Label>Type de conteneur*</Label>
                    <Select 
                      value={formData.type_conteneur} 
                      onValueChange={(value) => setFormData({ ...formData, type_conteneur: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez le type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20_pieds">20 pieds (TEU)</SelectItem>
                        <SelectItem value="40_pieds">40 pieds (FEU)</SelectItem>
                        <SelectItem value="40_hc">40 pieds High Cube (HC)</SelectItem>
                        <SelectItem value="20_open_top">20 pieds Open Top</SelectItem>
                        <SelectItem value="40_open_top">40 pieds Open Top</SelectItem>
                        <SelectItem value="20_flat_rack">20 pieds Flat Rack</SelectItem>
                        <SelectItem value="40_flat_rack">40 pieds Flat Rack</SelectItem>
                        <SelectItem value="20_reefer">20 pieds Reefer (Frigo)</SelectItem>
                        <SelectItem value="40_reefer">40 pieds Reefer (Frigo)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* === SECTION IMMOBILISATION === */}
              <div className="col-span-2 border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-3">Immobilisation</h3>
              </div>

              <div className="space-y-2">
                <Label>Jours d'immobilisation</Label>
                <Input 
                  type="number" 
                  min="0" 
                  value={formData.jours_immo} 
                  onChange={(e) => setFormData({ ...formData, jours_immo: e.target.value })} 
                  placeholder="0"
                />
                <p className="text-xs text-gray-500">Nombre de jours d'immobilisation du véhicule</p>
              </div>

              <div className="space-y-2">
                <Label>Tarif unitaire journalier (FCFA)</Label>
                <Input 
                  type="number" 
                  min="0" 
                  step="1000" 
                  value={formData.tarif_unitaire_immo} 
                  onChange={(e) => setFormData({ ...formData, tarif_unitaire_immo: e.target.value })} 
                  placeholder="50000"
                />
                <p className="text-xs text-gray-500">Coût journalier d'immobilisation</p>
              </div>

              {formData.jours_immo && formData.tarif_unitaire_immo && (
                <div className="col-span-2 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm font-semibold text-blue-900">
                    Coût total immobilisation: {(parseFloat(formData.jours_immo) * parseFloat(formData.tarif_unitaire_immo)).toLocaleString('fr-FR')} FCFA
                  </p>
                </div>
              )}

              <div className="col-span-2 space-y-2">
                <Label>Commentaire opérateur</Label>
                <Textarea value={formData.commentaire_operateur} onChange={(e) => setFormData({ ...formData, commentaire_operateur: e.target.value })} rows={3} data-testid="op-comment-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditMode(false); setWizardStep(0); }}>Annuler</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="op-submit-btn">Enregistrer</Button>
            </DialogFooter>
          </form>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          {/* Stats */}
          {documentsStats && (
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-blue-600">{documentsStats.total}</div>
                  <div className="text-sm text-gray-600">Total</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-green-600">{documentsStats.by_type?.bon_chargement || 0}</div>
                  <div className="text-sm text-gray-600">Bons chargement</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-purple-600">{documentsStats.by_type?.bon_dechargement || 0}</div>
                  <div className="text-sm text-gray-600">Bons déchargement</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Upload buttons */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Bon de chargement</Label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleDocumentUpload(e, 'bon_chargement')}
                className="hidden"
                id="upload-bon-chargement"
                disabled={uploadingDocument}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => document.getElementById('upload-bon-chargement').click()}
                disabled={uploadingDocument}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingDocument ? 'Upload...' : 'Téléverser'}
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Bon de déchargement</Label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleDocumentUpload(e, 'bon_dechargement')}
                className="hidden"
                id="upload-bon-dechargement"
                disabled={uploadingDocument}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => document.getElementById('upload-bon-dechargement').click()}
                disabled={uploadingDocument}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingDocument ? 'Upload...' : 'Téléverser'}
              </Button>
            </div>
          </div>

          {/* Documents list */}
          <div className="space-y-2">
            <Label>Documents ({documents.length})</Label>
            {documents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Aucun document pour cette opération
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {documents.map((doc) => (
                  <Card key={doc.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {doc.content_type?.startsWith('image/') ? (
                            <ImageIcon className="h-8 w-8 text-blue-600" />
                          ) : (
                            <File className="h-8 w-8 text-red-600" />
                          )}
                          <div>
                            <div className="font-medium">{doc.filename}</div>
                            <div className="text-sm text-gray-500">
                              {doc.type === 'bon_chargement' ? 'Bon de chargement' : 
                               doc.type === 'bon_dechargement' ? 'Bon de déchargement' : 
                               'Autre'} · {(doc.size_bytes / 1024).toFixed(1)} KB
                            </div>
                            <div className="text-xs text-gray-400">
                              {new Date(doc.uploaded_at).toLocaleString('fr-FR')}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleDocumentDownload(doc.id, doc.filename)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleDocumentDelete(doc.id)}
                          >
                            <X className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <div className="text-xs text-gray-500 mt-4">
            Formats acceptés: PDF, JPG, PNG (max 10 MB)
          </div>
        </TabsContent>
      </Tabs>
          ) : (
            // Mode création: utiliser Wizard
            <form id="operation-form" onSubmit={handleSubmit}>
              <Wizard
                steps={operationWizardSteps}
                currentStep={wizardStep}
                onNext={handleWizardNext}
                onPrev={handleWizardPrev}
                onSubmit={handleWizardSubmit}
                canProceed={true}
              >
                {renderWizardStepContent()}
              </Wizard>
            </form>
          )}
      </DialogContent>
    </Dialog>

      {/* Dialog Détails Opération */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails de l'opération</DialogTitle>
            <DialogDescription>
              Informations complètes sur l'opération {selectedOperation?.matricule}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOperation && (
            <div className="space-y-6">
              {/* Section 1: Informations générales */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-3">📋 Informations générales</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Matricule</p>
                    <p className="font-medium">{selectedOperation.matricule}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date opération</p>
                    <p className="font-medium">{selectedOperation.date_operation}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Statut</p>
                    <span className={`inline-block px-2 py-1 rounded text-sm ${
                      selectedOperation.statut === 'Terminée' ? 'bg-green-100 text-green-800' :
                      selectedOperation.statut === 'En cours' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedOperation.statut}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Client</p>
                    <p className="font-medium">{selectedOperation.client_nom || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Opérateur</p>
                    <p className="font-medium">{getOperateurNom(selectedOperation.operateur_assigne) || selectedOperation.operateur || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Pays</p>
                    <p className="font-medium">{selectedOperation.pays}</p>
                  </div>
                </div>
              </div>

              {/* Section 2: Trajet */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-3">🚚 Trajet et acteurs</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Départ</p>
                    <p className="font-medium">{selectedOperation.ville_depart || selectedOperation.pickup_city || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Destination</p>
                    <p className="font-medium">{selectedOperation.ville_destination || selectedOperation.delivery_city || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Transporteur</p>
                    <p className="font-medium">{selectedOperation.transporteur || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Chauffeur</p>
                    <p className="font-medium">{getChauffeurNom(selectedOperation.chauffeur_id) || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Section 3: Marchandise */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-3">📦 Marchandise et chargement</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Type de marchandise</p>
                    <p className="font-medium">{selectedOperation.type_marchandise || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Poids</p>
                    <p className="font-medium">{selectedOperation.poids_marchandise ? `${selectedOperation.poids_marchandise}T` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Volume</p>
                    <p className="font-medium">{selectedOperation.volume_marchandise ? `${selectedOperation.volume_marchandise}m³` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Conteneur</p>
                    <p className="font-medium">
                      {selectedOperation.est_conteneur ? `Oui - ${selectedOperation.type_conteneur || 'Type non précisé'}` : 'Non'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Date/heure chargement</p>
                    <p className="font-medium">
                      {selectedOperation.datetime_chargement ? 
                        new Date(selectedOperation.datetime_chargement).toLocaleString('fr-FR') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date/heure déchargement prévu</p>
                    <p className="font-medium">
                      {selectedOperation.datetime_dechargement_prevu ? 
                        new Date(selectedOperation.datetime_dechargement_prevu).toLocaleString('fr-FR') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date/heure déchargement réel</p>
                    <p className="font-medium">
                      {selectedOperation.datetime_dechargement_reel ? 
                        new Date(selectedOperation.datetime_dechargement_reel).toLocaleString('fr-FR') : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 4: Financier */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-3">💰 Informations financières</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Tarif transport</p>
                    <p className="font-medium">{selectedOperation.tarif_transport ? `${selectedOperation.tarif_transport.toLocaleString()} FCFA` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Jours immobilisation</p>
                    <p className="font-medium">{selectedOperation.jours_immobilisation || '0'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tarif immobilisation journalier</p>
                    <p className="font-medium">
                      {selectedOperation.tarif_immobilisation_journalier ? 
                        `${selectedOperation.tarif_immobilisation_journalier.toLocaleString()} FCFA` : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total immobilisation</p>
                    <p className="font-medium text-lg">
                      {(selectedOperation.jours_immobilisation && selectedOperation.tarif_immobilisation_journalier) ? 
                        `${(selectedOperation.jours_immobilisation * selectedOperation.tarif_immobilisation_journalier).toLocaleString()} FCFA` : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 5: Commentaire */}
              {selectedOperation.commentaire_operateur && (
                <div className="border-b pb-4">
                  <h3 className="font-semibold text-lg mb-3">💬 Commentaire</h3>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedOperation.commentaire_operateur}</p>
                </div>
              )}

              {/* Section 6: Documents classés par type */}
              <div className="space-y-6">
                <h3 className="font-semibold text-lg">📄 Documents</h3>
                
                {/* Documents de l'opération */}
                <div className="border rounded-lg p-4 bg-blue-50/50">
                  <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Documents de l'opération
                  </h4>
                  {documents && documents.length > 0 ? (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded ${
                              doc.type === 'bon_chargement' ? 'bg-blue-100 text-blue-600' :
                              doc.type === 'bon_dechargement' ? 'bg-green-100 text-green-600' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              <FileText className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {doc.type === 'bon_chargement' ? 'Bon de chargement' :
                                 doc.type === 'bon_dechargement' ? 'Bon de déchargement' :
                                 'Document'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {doc.filename} • {(doc.size_bytes / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const response = await api.get(
                                  `/operations/${selectedOperation.id}/documents/${doc.id}/download`,
                                  { responseType: 'blob' }
                                );
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', doc.filename);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success('Document téléchargé');
                              } catch (error) {
                                console.error('Download error:', error);
                                toast.error('Erreur lors du téléchargement');
                              }
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-2">Aucun document d'opération</p>
                  )}
                </div>

                {/* Documents du chauffeur */}
                {selectedOperation.chauffeur_id && (
                  <div className="border rounded-lg p-4 bg-amber-50/50">
                    <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Documents du chauffeur
                      {selectedOperation.chauffeur_nom && (
                        <span className="text-sm font-normal text-amber-600">({selectedOperation.chauffeur_nom})</span>
                      )}
                    </h4>
                    <DocumentsChauffeurSection chauffeurId={selectedOperation.chauffeur_id} />
                  </div>
                )}

                {/* Documents du véhicule */}
                {selectedOperation.vehicule_id && (
                  <div className="border rounded-lg p-4 bg-emerald-50/50">
                    <h4 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
                      <Truck className="h-5 w-5" />
                      Documents du véhicule
                      {selectedOperation.immatriculation && (
                        <span className="text-sm font-normal text-emerald-600">({selectedOperation.immatriculation})</span>
                      )}
                    </h4>
                    <DocumentsVehiculeSection vehiculeId={selectedOperation.vehicule_id} />
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Validation avant clôture */}
      <Dialog open={validationDialogOpen} onOpenChange={setValidationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>⚠️ Informations manquantes</DialogTitle>
            <DialogDescription>
              Avant de clôturer cette opération, veuillez renseigner les informations suivantes :
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {operationToClose && operationToClose.missingFields && (
              <div className="bg-red-50 border border-red-200 rounded p-4">
                <p className="font-semibold text-red-800 mb-2">Champs obligatoires manquants :</p>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  {operationToClose.missingFields.includes('datetime_chargement_reel') && (
                    <li className="text-red-600">
                      <strong>Date et heure de chargement réel</strong>
                      <p className="ml-5 text-xs text-gray-600">Quand le chargement a-t-il réellement eu lieu ?</p>
                    </li>
                  )}
                  {operationToClose.missingFields.includes('datetime_dechargement_reel') && (
                    <li className="text-red-600">
                      <strong>Date et heure de déchargement réel</strong>
                      <p className="ml-5 text-xs text-gray-600">Quand le déchargement a-t-il réellement eu lieu ?</p>
                    </li>
                  )}
                  {operationToClose.missingFields.includes('bon_chargement_id') && (
                    <li className="text-red-600">
                      <strong>Bon de chargement</strong>
                      <p className="ml-5 text-xs text-gray-600">Document de chargement requis</p>
                    </li>
                  )}
                  {operationToClose.missingFields.includes('jours_immobilisation') && (
                    <li className="text-orange-600">
                      <strong>Jours d'immobilisation</strong>
                      <p className="ml-5 text-xs text-gray-600">Nombre de jours où le véhicule est resté immobilisé (mettre 0 si aucun)</p>
                    </li>
                  )}
                </ul>
              </div>
            )}
            
            <p className="text-sm text-gray-600">
              Ces informations sont nécessaires pour calculer les coûts réels et finaliser l'opération.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setValidationDialogOpen(false);
                setOperationToClose(null);
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                // Rester dans le formulaire actuel, ne pas fermer
                setValidationDialogOpen(false);
                setOperationToClose(null);
                // Le formulaire reste ouvert pour compléter
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Compléter maintenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de clôture d'opération */}
      <Dialog open={closureDialogOpen} onOpenChange={setClosureDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🏁 Clôture de l'opération - {operationToClose?.matricule}
            </DialogTitle>
            <DialogDescription>
              Suivez les étapes pour terminer cette opération
            </DialogDescription>
          </DialogHeader>
          
          <Wizard
            steps={closureWizardSteps}
            currentStep={closureWizardStep}
            onNext={handleClosureNext}
            onPrev={handleClosurePrev}
            onSubmit={handleClosureSubmit}
            canProceed={canProceedClosureStep()}
          >
          <div className="space-y-6 py-4">
            {/* Étape 1: Dates et heures réelles */}
            {closureWizardStep === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h3 className="flex items-center gap-2 font-semibold text-amber-900 mb-4">
                  📅 Dates et heures réelles (obligatoires)
                </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Date de départ réelle */}
                <div className="space-y-2">
                  <Label htmlFor="closure-date-depart">Date de départ réelle *</Label>
                  <Input
                    id="closure-date-depart"
                    type="date"
                    value={closureData.date_depart_reel}
                    onChange={(e) => setClosureData({...closureData, date_depart_reel: e.target.value})}
                    required
                  />
                </div>

                {/* Heure de départ réelle */}
                <div className="space-y-2">
                  <Label htmlFor="closure-heure-depart">Heure de départ réelle *</Label>
                  <Input
                    id="closure-heure-depart"
                    type="time"
                    value={closureData.heure_depart_reel}
                    onChange={(e) => setClosureData({...closureData, heure_depart_reel: e.target.value})}
                    required
                  />
                </div>

                {/* Date d'arrivée réelle */}
                <div className="space-y-2">
                  <Label htmlFor="closure-date-arrivee">Date d'arrivée réelle *</Label>
                  <Input
                    id="closure-date-arrivee"
                    type="date"
                    value={closureData.date_arrivee_reel}
                    onChange={(e) => setClosureData({...closureData, date_arrivee_reel: e.target.value})}
                    required
                  />
                </div>

                {/* Heure d'arrivée réelle */}
                <div className="space-y-2">
                  <Label htmlFor="closure-heure-arrivee">Heure d'arrivée réelle *</Label>
                  <Input
                    id="closure-heure-arrivee"
                    type="time"
                    value={closureData.heure_arrivee_reel}
                    onChange={(e) => setClosureData({...closureData, heure_arrivee_reel: e.target.value})}
                    required
                  />
                </div>
              </div>
              </div>
            )}

            {/* Étape 2: Upload Documents */}
            {closureWizardStep === 1 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="flex items-center gap-2 font-semibold text-blue-900 mb-4">
                  📎 Documents obligatoires
                </h3>
              
              <div className="grid grid-cols-1 gap-4">
                {/* Bon de chargement */}
                <div className="space-y-2">
                  <Label htmlFor="closure-bon-chargement" className="flex items-center gap-2">
                    <span className="text-red-600">*</span>
                    Bon de chargement
                  </Label>
                  <Input
                    id="closure-bon-chargement"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setClosureData({...closureData, bon_chargement_file: e.target.files[0]})}
                    required
                  />
                  {closureData.bon_chargement_file && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      ✓ {closureData.bon_chargement_file.name}
                    </p>
                  )}
                </div>

                {/* Bon de déchargement */}
                <div className="space-y-2">
                  <Label htmlFor="closure-bon-dechargement" className="flex items-center gap-2">
                    <span className="text-red-600">*</span>
                    Bon de déchargement
                  </Label>
                  <Input
                    id="closure-bon-dechargement"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setClosureData({...closureData, bon_dechargement_file: e.target.files[0]})}
                    required
                  />
                  {closureData.bon_dechargement_file && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      ✓ {closureData.bon_dechargement_file.name}
                    </p>
                  )}
                </div>
              </div>
              </div>
            )}

            {/* Étape 3: Incidents rencontrés */}
            {closureWizardStep === 2 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="flex items-center gap-2 font-semibold text-red-900 mb-3">
                  ⚠️ Incidents rencontrés
                </h3>
              
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="incidents-non"
                    name="incidents"
                    checked={!closureData.incidents_rencontres}
                    onChange={() => {
                      setClosureData({...closureData, incidents_rencontres: false});
                      setShowIncidentForm(false);
                    }}
                    className="w-4 h-4 text-green-600"
                  />
                  <Label htmlFor="incidents-non" className="cursor-pointer text-green-700 font-medium">
                    Non - Aucun incident
                  </Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="incidents-oui"
                    name="incidents"
                    checked={closureData.incidents_rencontres}
                    onChange={() => {
                      setClosureData({...closureData, incidents_rencontres: true});
                      setShowIncidentForm(true);
                    }}
                    className="w-4 h-4 text-red-600"
                  />
                  <Label htmlFor="incidents-oui" className="cursor-pointer text-red-700 font-medium">
                    Oui - Incident rencontré
                  </Label>
                </div>
              </div>
              
              {/* Formulaire de déclaration d'incident */}
              {showIncidentForm && (
                <div className="space-y-4 pt-4 border-t border-red-300">
                  <h4 className="font-semibold text-red-900">📝 Déclarer l'incident</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Type d'incident */}
                    <div className="space-y-2">
                      <Label htmlFor="incident-type">Type d'incident *</Label>
                      <Select 
                        value={incidentData.type_incident} 
                        onValueChange={(value) => setIncidentData({...incidentData, type_incident: value})}
                      >
                        <SelectTrigger id="incident-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Accident">Accident</SelectItem>
                          <SelectItem value="Panne">Panne</SelectItem>
                          <SelectItem value="Retard">Retard</SelectItem>
                          <SelectItem value="Perte marchandise">Perte marchandise</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Gravité */}
                    <div className="space-y-2">
                      <Label htmlFor="incident-gravite">Gravité *</Label>
                      <Select 
                        value={incidentData.gravite} 
                        onValueChange={(value) => setIncidentData({...incidentData, gravite: value})}
                      >
                        <SelectTrigger id="incident-gravite">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Faible">Faible</SelectItem>
                          <SelectItem value="Moyenne">Moyenne</SelectItem>
                          <SelectItem value="Élevée">Élevée</SelectItem>
                          <SelectItem value="Critique">Critique</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="incident-description">Description * (détails de l'incident)</Label>
                    <Textarea
                      id="incident-description"
                      value={incidentData.description}
                      onChange={(e) => setIncidentData({...incidentData, description: e.target.value})}
                      placeholder="Décrivez l'incident survenu..."
                      rows={3}
                      required
                    />
                  </div>
                  
                  {/* Pièce jointe */}
                  <div className="space-y-2">
                    <Label htmlFor="incident-file">Pièce jointe (optionnelle)</Label>
                    <Input
                      id="incident-file"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setIncidentData({...incidentData, piece_jointe: e.target.files[0]})}
                    />
                    {incidentData.piece_jointe && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        ✓ {incidentData.piece_jointe.name}
                      </p>
                    )}
                  </div>
                  
                  {/* Informations héritées */}
                  {operationToClose && (
                    <div className="bg-white p-3 rounded border border-red-200">
                      <p className="text-xs font-semibold text-red-900 mb-2">Informations héritées automatiquement:</p>
                      <ul className="text-xs space-y-1 text-gray-700">
                        <li>✓ Opération: {operationToClose.matricule}</li>
                        <li>✓ Chauffeur: {getChauffeurNom(operationToClose.chauffeur_id)}</li>
                        <li>✓ Véhicule: {operationToClose.vehicle_snapshot?.immatriculation || 'N/A'}</li>
                        <li>✓ Client: {operationToClose.client_nom || 'N/A'}</li>
                        <li>✓ Date incident: {new Date().toLocaleString('fr-FR')}</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
              </div>
            )}

            {/* Étape 4: Récapitulatif */}
            {closureWizardStep === 3 && operationToClose && (
              <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    📋 Récapitulatif de l'opération
                  </h3>
                  
                  {/* Informations opération */}
                  <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b">
                    <div>
                      <span className="text-sm text-gray-600">Matricule:</span>
                      <span className="ml-2 font-medium">{operationToClose.matricule}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Client:</span>
                      <span className="ml-2 font-medium">{operationToClose.client_nom || '-'}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Itinéraire:</span>
                      <span className="ml-2 font-medium">
                        {operationToClose.ville_depart} → {operationToClose.ville_destination}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Chauffeur:</span>
                      <span className="ml-2 font-medium">{getChauffeurNom(operationToClose.chauffeur_id) || '-'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm text-gray-600">Véhicule:</span>
                      <span className="ml-2 font-medium">{operationToClose.vehicle_snapshot?.immatriculation || '-'}</span>
                    </div>
                  </div>
                  
                  {/* Dates saisies */}
                  <div className="mb-4 pb-4 border-b">
                    <h4 className="font-semibold text-sm text-gray-900 mb-2">Dates et heures réelles</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Chargement:</span>
                        <span className="ml-2 font-medium text-green-700">
                          {closureData.date_depart_reel} à {closureData.heure_depart_reel}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Déchargement:</span>
                        <span className="ml-2 font-medium text-green-700">
                          {closureData.date_arrivee_reel} à {closureData.heure_arrivee_reel}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Documents */}
                  <div className="mb-4 pb-4 border-b">
                    <h4 className="font-semibold text-sm text-gray-900 mb-2">Documents joints</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        <span>Bon de chargement: {closureData.bon_chargement_file?.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        <span>Bon de déchargement: {closureData.bon_dechargement_file?.name}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Incidents */}
                  <div>
                    <h4 className="font-semibold text-sm text-gray-900 mb-2">Incidents</h4>
                    {closureData.incidents_rencontres ? (
                      <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                        <p className="font-medium text-red-900 mb-1">
                          ⚠️ Incident déclaré: {incidentData.type_incident}
                        </p>
                        <p className="text-gray-700">
                          <span className="font-medium">Gravité:</span> {incidentData.gravite}
                        </p>
                        <p className="text-gray-700 mt-1">
                          <span className="font-medium">Description:</span> {incidentData.description}
                        </p>
                        {incidentData.piece_jointe && (
                          <p className="text-gray-700 mt-1">
                            <span className="font-medium">Pièce jointe:</span> {incidentData.piece_jointe.name}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-green-700 flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        Aucun incident rencontré
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-900">
                    <strong>⚠️ Attention:</strong> En clôturant cette opération, vous confirmez que toutes les informations sont correctes et complètes. Cette action est irréversible.
                  </p>
                </div>
              </div>
            )}
          </div>
          </Wizard>
        </DialogContent>
      </Dialog>

      {/* Dialog pour facturation (passage au statut "Facturé") */}
      <Dialog open={billingDialogOpen} onOpenChange={setBillingDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              💰 Facturation - {operationToBill?.matricule}
            </DialogTitle>
            <DialogDescription>
              Saisissez les informations de tarification client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Tarif Transporteur (lecture seule) */}
            <div className="space-y-2">
              <Label>Tarif Transporteur (FCFA)</Label>
              <Input 
                type="number" 
                value={billingData.tarif_transporteur}
                readOnly
                className="bg-gray-50"
              />
            </div>
            
            {/* Tarif Client (saisie manuelle) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Tarif Client (FCFA) *</Label>
              <Input 
                type="number"
                min="0"
                step="1000"
                value={billingData.tarif_client}
                onChange={(e) => setBillingData({...billingData, tarif_client: e.target.value})}
                className="font-bold text-emerald-700"
                placeholder="Saisissez le tarif client"
                required
              />
            </div>
            
            {/* Marge % (calculée automatiquement) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Marge (%)
                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">✓ Calculée</span>
              </Label>
              <Input 
                type="text"
                value={billingData.marge_percent ? `${parseFloat(billingData.marge_percent).toFixed(2)}%` : '-'}
                readOnly
                className={`font-medium ${parseFloat(billingData.marge_percent) < 15 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}
              />
            </div>
            
            {/* Warning si marge < 15% */}
            {billingData.marge_percent && parseFloat(billingData.marge_percent) < 15 && (
              <div className="text-xs text-red-600 bg-red-50 p-3 rounded border border-red-200 flex items-start gap-2">
                <span className="text-base">⚠️</span>
                <span>
                  <strong>Attention:</strong> La marge est inférieure à 15% ({parseFloat(billingData.marge_percent).toFixed(2)}%). Assurez-vous que ce tarif couvre vos coûts.
                </span>
              </div>
            )}
            
            {/* Formule affichée */}
            {billingData.tarif_transporteur && billingData.tarif_client && (
              <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded border border-blue-200">
                <span className="font-mono">
                  Marge = ({parseFloat(billingData.tarif_client).toLocaleString()} - {parseFloat(billingData.tarif_transporteur).toLocaleString()}) / {parseFloat(billingData.tarif_transporteur).toLocaleString()} × 100 = {billingData.marge_percent ? parseFloat(billingData.marge_percent).toFixed(2) : '...'}%
                </span>
              </div>
            )}
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setBillingDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleBillingSubmit} disabled={!billingData.tarif_client}>
              Facturer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

          </div>
        </TabsContent>

        {/* Contenu onglet Carte temps réel */}
        <TabsContent value="carte" className="flex-1 m-0 h-full">
          <div className="h-full">
            <RealtimeMapOSM />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Operations;
